import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Plus, Trash2, Camera, Edit3, X, ChevronDown, ChevronUp, Search, Check, AlertCircle, Clock, Lock, Loader2, Image } from 'lucide-react';
import { toast } from 'sonner';
import AddCustomerModal from '../components/AddCustomerModal';
import EditCustomerModal from '../components/EditCustomerModal';
import CustomerSelectionModal from '../components/CustomerSelectionModal';
import ImageEditorModal from '../components/ImageEditorModal';
import SignatureModal from '../components/SignatureModal';
import SignatureCanvas from 'react-signature-canvas';
import { QuantityPickerModal, PurityPickerModal, FinishColorPickerModal, VendorPickerModal } from '../components/ModalPicker';
import { RatesSection } from '../components/RatesSection';
import { PaymentTypeSelector, CashPaymentModal, CardPaymentModal, UPIPaymentModal } from '../components/PaymentModals';
import { BankTransferModal, OldGoldModal } from '../components/PaymentModalsExtended';
import { MetalCreditModal } from '../components/MetalCreditModalNew';
import { PaymentSummary } from '../components/PaymentSummary';
import PaymentReminderCard from '../components/PaymentReminderCard';
import useExitConfirmation from '../hooks/useExitConfirmation';
import { uploadToCloudinary, uploadMultipleToCloudinary, isBase64DataUrl, isCloudinaryUrl } from '../services/cloudinaryService';
import ReviewPageErrorBoundary from '../components/ReviewPageErrorBoundary';

const CreateOrderStepper = ({ user, onLogout, editMode = false, existingOrder = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sizePresets, setSizePresets] = useState([]);
  const [makingPresets, setMakingPresets] = useState([]);
  const [signatureNames, setSignatureNames] = useState([]);
  
  // Customer
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  
  // Items
  const [items, setItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [showInternalRemark, setShowInternalRemark] = useState({});
  const [editingImage, setEditingImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdOrderData, setCreatedOrderData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Inline Signatures (no modal)
  const [managementSignature, setManagementSignature] = useState(null);
  const [managementSignatureName, setManagementSignatureName] = useState('');
  const [customerSignature, setCustomerSignature] = useState(null);
  const [showManagementSignatureModal, setShowManagementSignatureModal] = useState(false);
  const [showCustomerSignatureModal, setShowCustomerSignatureModal] = useState(false);
  const managementSigRef = useRef(null);
  const customerSigRef = useRef(null);
  
  // Rates
  const [rateMode, setRateMode] = useState('single'); // single | per-item
  const [ratePolicy, setRatePolicy] = useState('none'); // lock-today | rate-at-delivery | none
  const [lockDate, setLockDate] = useState('');
  
  // GST and Tax settings
  const [taxScope, setTaxScope] = useState('TAXABLE'); // KACCHA | TAXABLE
  const [gstApplicable, setGstApplicable] = useState(true);
  const [gstInputStyle, setGstInputStyle] = useState('EXCL'); // EXCL | INCL
  const [gstPercent, setGstPercent] = useState(3.0);
  
  // Master Data from Settings
  const [itemTypes, setItemTypes] = useState([]);
  const [purityOptions, setPurityOptions] = useState([]);
  const [finishColors, setFinishColors] = useState([]);
  
  // Fallback hardcoded arrays (if API fails)
  const fallbackItemTypes = ['Ring', 'Chain', 'Bangle', 'Bracelet', 'Necklace', 'Earring', 'Pendant', 'Nose Pin', 'Anklet', 'Mangalsutra', 'Haar', 'Other'];
  const fallbackPurities = ['24K', '22K', '18K'];
  const fallbackFinishColors = ['Yellow Gold', 'Rose Gold', 'White Gold', 'Two Tone', 'Tri Color', 'Antique', 'Rhodium', 'Other'];
  
  const [purityMultipliers, setPurityMultipliers] = useState({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
  
  // Rate data
  const [base24kRate, setBase24kRate] = useState('');
  const [purityRates, setPurityRates] = useState({});
  const [purityRateLinks, setPurityRateLinks] = useState({}); // Track which rates are linked to base
  const [itemRates, setItemRates] = useState({});
  
  // Legacy
  const [singleRate, setSingleRate] = useState('');
  const [lockedRate, setLockedRate] = useState(null);
  
  // Order Date
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [pendingOrderDate, setPendingOrderDate] = useState('');
  
  // Delivery Dates
  const [deliveryMode, setDeliveryMode] = useState('single'); // single | per-item
  const [singleDeliveryDate, setSingleDeliveryDate] = useState('');
  const [itemDeliveryDates, setItemDeliveryDates] = useState({});
  
  // Payment Reminder
  const [showPaymentReminder, setShowPaymentReminder] = useState(false);
  const [paymentReminderData, setPaymentReminderData] = useState(null);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  
  // Item Type Modal
  const [showItemTypeModal, setShowItemTypeModal] = useState(false);
  const [itemTypeModalTargetId, setItemTypeModalTargetId] = useState(null);
  const [itemTypeSearch, setItemTypeSearch] = useState('');
  
  // Review & Finalize
  const [showReviewPage, setShowReviewPage] = useState(false);
  
  // Payment
  const [payments, setPayments] = useState([]);
  const [showPaymentTypeSelector, setShowPaymentTypeSelector] = useState(false);
  const [activePaymentModal, setActivePaymentModal] = useState(null); // cash, card, upi, bank, old-gold, metal
  const [metalCreditBalance, setMetalCreditBalance] = useState(0); // Total metal credit in grams
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePaymentIndex, setDeletePaymentIndex] = useState(null);
  
  // Modal pickers state
  const [activeModal, setActiveModal] = useState(null);
  const [modalItemId, setModalItemId] = useState(null);
  
  // Section states
  const [expandedSections, setExpandedSections] = useState({
    items: true,
    rates: false,
    dates: false,
    payment: false,
    review: false
  });
  
  const [loading, setLoading] = useState(false);
  
  // Refs for scrolling
  const itemsRef = useRef(null);
  const ratesRef = useRef(null);
  const datesRef = useRef(null);
  const paymentRef = useRef(null);
  const reviewRef = useRef(null);

  // Exit confirmation - show warning when user has unsaved data
  const hasUnsavedChanges = !showSuccessModal && (
    selectedCustomer || 
    items.length > 0 || 
    payments.length > 0 ||
    managementSignature ||
    customerSignature
  );
  const { confirmNavigation } = useExitConfirmation(
    hasUnsavedChanges && !editMode,
    'You have unsaved order data. Are you sure you want to leave? All entered information will be lost.'
  );

  // Wrap confirmNavigation to clear saved state when user confirms exit
  const handleConfirmExit = (navigateFn) => {
    confirmNavigation(() => {
      clearSavedState(); // Clear sessionStorage on confirmed exit
      navigateFn();
    });
  };

  // Auto-save state to sessionStorage to handle page reloads from camera
  const saveFormState = () => {
    try {
      // Strip all base64 image data — photos are large and will exceed storage quota
      // Only keep Cloudinary URLs (already uploaded), discard local base64 previews
      const stripPhotos = (itemList) => (itemList || []).map(item => ({
        ...item,
        photos: (item.photos || []).filter(p => typeof p === 'string' && !p.startsWith('data:'))
      }));

      const stripSignature = (sig) => (sig && sig.startsWith('data:') ? null : sig);

      const formState = {
        items: stripPhotos(items),
        selectedCustomer,
        payments: (payments || []).map(p => ({
          ...p,
          // Strip any base64 images inside payment objects too
          receipt_image: p.receipt_image && !p.receipt_image.startsWith('data:') ? p.receipt_image : undefined,
        })),
        base24kRate,
        purityRates,
        rateMode,
        ratePolicy,
        orderDate,
        deliveryMode,
        singleDeliveryDate,
        itemDeliveryDates,
        taxScope,
        gstApplicable,
        gstInputStyle,
        gstPercent,
        paymentReminderData,
        managementSignature: stripSignature(managementSignature),
        managementSignatureName,
        customerSignature: stripSignature(customerSignature),
        timestamp: Date.now()
      };

      const serialized = JSON.stringify(formState);
      sessionStorage.setItem('orderFormState', serialized);
    } catch (e) {
      // If storage is still full (e.g. many Cloudinary URLs), silently skip — form state is non-critical
      if (e.name === 'QuotaExceededError') {
        console.warn('sessionStorage quota exceeded — skipping form state save');
        try { sessionStorage.removeItem('orderFormState'); } catch (_) {}
      }
    }
  };

  // Restore state from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('orderFormState');
    if (savedState && !editMode && !location.state?.fromBooking) {
      try {
        const formState = JSON.parse(savedState);
        // Only restore if saved within last 2 minutes (120 seconds) to avoid stale data
        // This ensures we only restore after camera usage, not after user left and came back
        if (formState.timestamp && (Date.now() - formState.timestamp) < 120000) {
          console.log('Restoring form state from sessionStorage (recent camera usage)');
          if (formState.items && formState.items.length > 0) setItems(formState.items);
          if (formState.selectedCustomer) setSelectedCustomer(formState.selectedCustomer);
          if (formState.payments) setPayments(formState.payments);
          if (formState.base24kRate) setBase24kRate(formState.base24kRate);
          if (formState.purityRates) setPurityRates(formState.purityRates);
          if (formState.rateMode) setRateMode(formState.rateMode);
          if (formState.ratePolicy) setRatePolicy(formState.ratePolicy);
          if (formState.orderDate) setOrderDate(formState.orderDate);
          if (formState.deliveryMode) setDeliveryMode(formState.deliveryMode);
          if (formState.singleDeliveryDate) setSingleDeliveryDate(formState.singleDeliveryDate);
          if (formState.itemDeliveryDates) setItemDeliveryDates(formState.itemDeliveryDates);
          if (formState.taxScope) setTaxScope(formState.taxScope);
          if (formState.gstApplicable !== undefined) setGstApplicable(formState.gstApplicable);
          if (formState.gstInputStyle) setGstInputStyle(formState.gstInputStyle);
          if (formState.gstPercent) setGstPercent(formState.gstPercent);
          if (formState.paymentReminderData) setPaymentReminderData(formState.paymentReminderData);
          if (formState.managementSignature) setManagementSignature(formState.managementSignature);
          if (formState.managementSignatureName) setManagementSignatureName(formState.managementSignatureName);
          if (formState.customerSignature) setCustomerSignature(formState.customerSignature);
          // Clear immediately after restoration to prevent future accidental restores
          sessionStorage.removeItem('orderFormState');
        } else {
          // Data is too old, clear it
          console.log('Clearing old form state from sessionStorage');
          sessionStorage.removeItem('orderFormState');
        }
      } catch (err) {
        console.error('Error restoring form state:', err);
        sessionStorage.removeItem('orderFormState');
      }
    } else {
      // If in edit mode or from booking, clear any saved state to avoid conflicts
      sessionStorage.removeItem('orderFormState');
    }
  }, []);

  // Auto-save state whenever critical data changes
  useEffect(() => {
    if (items.length > 0 || selectedCustomer || payments.length > 0) {
      saveFormState();
    }
  }, [items, selectedCustomer, payments, base24kRate, purityRates, orderDate, deliveryMode, singleDeliveryDate]);

  // Clear saved state on successful submission
  const clearSavedState = () => {
    sessionStorage.removeItem('orderFormState');
  };

  useEffect(() => {
    loadCustomers();
    loadVendors();
    loadPresets();
    loadSignatureNames();
    
    // Auto-set management signature name to current user's username
    if (user?.username) {
      setManagementSignatureName(user.username);
    }
  }, []);
  
  // Handle booking conversion - pre-populate from booking data
  useEffect(() => {
    if (location.state?.fromBooking && location.state?.bookingData) {
      const booking = location.state.bookingData;
      
      // Pre-populate customer
      if (booking.customer_id) {
        setSelectedCustomer(booking.customer_id);
      }
      
      // Pre-populate advance payments
      if (booking.advance_payments && booking.advance_payments.length > 0) {
        setPayments(booking.advance_payments);
      }
      
      // Pre-populate rate policy
      if (booking.rate_policy && booking.rate_policy !== 'none') {
        setRatePolicy(booking.rate_policy);
        setRateMode(booking.rate_mode || 'single');
        if (booking.base24k_rate_ex_gst) {
          setBase24kRate(booking.base24k_rate_ex_gst);
        }
        if (booking.purity_rates) {
          setPurityRates(booking.purity_rates);
        }
        if (booking.lock_date) {
          setLockDate(booking.lock_date);
        }
      }
      
      // Pre-populate tax settings
      if (booking.tax_scope) {
        setTaxScope(booking.tax_scope);
      }
      if (booking.gst_applicable !== undefined) {
        setGstApplicable(booking.gst_applicable);
      }
      if (booking.gst_input_style) {
        setGstInputStyle(booking.gst_input_style);
      }
      if (booking.gst_percent) {
        setGstPercent(booking.gst_percent);
      }
      
      // Show a toast notification
      toast.success(`Converting booking ${booking.booking_id} to order. Customer and payment data pre-filled.`);
    }
  }, [location.state]);

  // Pre-fill form when editing existing order
  useEffect(() => {
    if (editMode && existingOrder) {
      // Auto-expand all sections in edit mode
      setExpandedSections({
        items: true,
        rates: true,
        dates: true,
        payment: true,
        review: false
      });
      
      // Pre-fill customer
      setSelectedCustomer(existingOrder.customer_id);
      
      // Pre-fill order date (CRITICAL: preserve original order date, don't reset to current date)
      if (existingOrder.order_date) {
        setOrderDate(existingOrder.order_date);
      } else if (existingOrder.created_at) {
        // Fallback to created_at if order_date is missing
        setOrderDate(existingOrder.created_at.split('T')[0]);
      }
      
      // Pre-fill items with ALL data including images and delivery dates
      const itemsWithFullData = (existingOrder.items || []).map(item => ({
        ...item,
        id: item.id || item.sub_order_id || Date.now() + Math.random(), // Ensure unique ID for React keys
        // Ensure photos array exists and is preserved
        photos: item.photos || [],
        // Ensure delivery date is preserved
        delivery_date: item.delivery_date || null,
        // Add production lock status
        isLocked: false // Will be set after checking production status
      }));
      setItems(itemsWithFullData);
      
      // Pre-fill rate settings
      setRateMode(existingOrder.rate_mode || 'single');
      setRatePolicy(existingOrder.rate_policy);
      setBase24kRate(existingOrder.base24k_rate_ex_gst || '');
      setPurityRates(existingOrder.purity_rates || {});
      
      // Pre-fill per-item rates in edit mode - remap from index-based keys to new item IDs
      if (existingOrder.item_rates && Object.keys(existingOrder.item_rates).length > 0) {
        const remappedItemRates = {};
        transformedItems.forEach((item, index) => {
          const itemKey = `item_${index}`;
          if (existingOrder.item_rates[itemKey]) {
            remappedItemRates[item.id] = existingOrder.item_rates[itemKey];
          }
        });
        setItemRates(remappedItemRates);
      } else {
        setItemRates({});
      }
      
      setTaxScope(existingOrder.tax_scope || 'TAXABLE');
      setGstApplicable(existingOrder.gst_applicable !== false);
      setGstInputStyle(existingOrder.gst_input_style || 'EXCL');
      
      // Pre-fill delivery mode and dates
      // Check if there's a single_delivery_date at order level
      if (existingOrder.single_delivery_date) {
        setDeliveryMode('single');
        setSingleDeliveryDate(existingOrder.single_delivery_date);
      } else {
        // Check if items have different delivery dates
        const hasMultipleDates = itemsWithFullData.some((item, idx) => 
          idx > 0 && item.delivery_date !== itemsWithFullData[0].delivery_date
        );
        setDeliveryMode(hasMultipleDates ? 'per-item' : 'single');
        
        if (!hasMultipleDates && itemsWithFullData.length > 0 && itemsWithFullData[0].delivery_date) {
          setSingleDeliveryDate(itemsWithFullData[0].delivery_date);
        }
      }
      
      // Pre-fill item delivery dates (use item.id as key which we just set above)
      const deliveryDates = {};
      itemsWithFullData.forEach(item => {
        if (item.delivery_date) {
          deliveryDates[item.id] = item.delivery_date;
        }
      });
      setItemDeliveryDates(deliveryDates);
      
      // Pre-fill signatures if they exist
      if (existingOrder.management_signature) {
        setManagementSignature(existingOrder.management_signature);
      }
      if (existingOrder.management_signature_name) {
        setManagementSignatureName(existingOrder.management_signature_name);
      }
      if (existingOrder.customer_signature) {
        setCustomerSignature(existingOrder.customer_signature);
      }
      
      // Pre-fill payments
      setPayments(existingOrder.advance_payments || []);
      
      // Pre-fill payment reminder if it exists
      if (existingOrder.reminder_date) {
        // Load the full reminder from the reminders API
        loadExistingReminder(existingOrder.order_id);
      }
    }
  }, [editMode, existingOrder]);
  
  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const loadVendors = async () => {
    try {
      const res = await axiosInstance.get('/vendors');
      setVendors(res.data);
    } catch (err) {
      console.error('Error loading vendors:', err);
    }
  };

  const loadPresets = async () => {
    try {
      const [sizeRes, makingRes, gstRes, itemTypesRes, purityRes, finishRes] = await Promise.all([
        axiosInstance.get('/masters/size-presets'),
        axiosInstance.get('/masters/making-presets'),
        axiosInstance.get('/masters/gst-config'),
        axiosInstance.get('/masters/item-types'),
        axiosInstance.get('/masters/purity-options'),
        axiosInstance.get('/masters/finish-colors')
      ]);
      setSizePresets(sizeRes.data);
      setMakingPresets(makingRes.data);
      
      // Load GST config
      if (gstRes.data) {
        setGstPercent(gstRes.data.gst_percent || 3.0);
      }
      
      // Set master data (filter active only)
      const activeItemTypes = (itemTypesRes.data || [])
        .filter(item => item.is_active !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(item => item.name);
      setItemTypes(activeItemTypes.length > 0 ? activeItemTypes : fallbackItemTypes);
      
      const activePurities = (purityRes.data || [])
        .filter(item => item.is_active !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      
      if (activePurities.length > 0) {
        setPurityOptions(activePurities.map(p => p.name));
        // Build purity multipliers object
        const multipliers = {};
        activePurities.forEach(p => {
          multipliers[p.name] = p.multiplier;
        });
        setPurityMultipliers(multipliers);
      } else {
        setPurityOptions(fallbackPurities);
        setPurityMultipliers({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
      }
      
      const activeFinishes = (finishRes.data || [])
        .filter(item => item.is_active !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(item => item.name);
      setFinishColors(activeFinishes.length > 0 ? activeFinishes : fallbackFinishColors);
      
    } catch (err) {
      console.error('Error loading presets:', err);
      // Fallback to hardcoded arrays if API fails
      setItemTypes(fallbackItemTypes);
      setPurityOptions(fallbackPurities);
      setFinishColors(fallbackFinishColors);
      setPurityMultipliers({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
    }
  };

  const loadSignatureNames = async () => {
    try {
      const res = await axiosInstance.get('/masters/signature-names');
      setSignatureNames(res.data);
    } catch (err) {
      console.error('Error loading signature names:', err);
      // Use defaults if API fails
      setSignatureNames([
        { id: 'sk', name: 'SK' },
        { id: 'mk', name: 'MK' },
        { id: 'av', name: 'AV' }
      ]);
    }
  };

  const loadExistingReminder = async (orderId) => {
    try {
      const res = await axiosInstance.get('/reminders', {
        params: { order_id: orderId }
      });
      if (res.data && res.data.length > 0) {
        const reminder = res.data[0];
        // Set payment reminder data to pre-fill the form
        setPaymentReminderData({
          enabled: true,
          due_at: reminder.due_at,
          amount_due: reminder.amount_due,
          channels: reminder.channels || [],
          reason_tags: reminder.reason_tags || [],
          notes: reminder.notes || '',
          auto_snooze: reminder.auto_snooze || null
        });
        // Expand payment reminder section when loading existing reminder
        setShowPaymentReminder(true);
        console.log('Loaded existing reminder:', reminder);
      }
    } catch (err) {
      console.log('No existing reminder found');
    }
  };

  // Auto-compute purity rates from base 24K rate
  const computePurityRates = (base24k) => {
    const computed = {};
    const links = {};
    Object.keys(purityMultipliers).forEach(purity => {
      if (purity !== '24K') {
        computed[purity] = Math.round(parseFloat(base24k) * purityMultipliers[purity]);
        links[purity] = true; // Initially all are linked
      }
    });
    setPurityRates(computed);
    setPurityRateLinks(links);
  };

  // Handle base 24K rate change
  const handleBase24kChange = (value) => {
    setBase24kRate(value);
    if (value && !isNaN(value)) {
      computePurityRates(value);
    } else {
      setPurityRates({});
      setPurityRateLinks({});
    }
  };

  // Toggle link/unlink for a purity rate
  const togglePurityLink = (purity) => {
    setPurityRateLinks(prev => ({
      ...prev,
      [purity]: !prev[purity]
    }));
    
    // If re-linking, recompute from base
    if (!purityRateLinks[purity] && base24kRate) {
      setPurityRates(prev => ({
        ...prev,
        [purity]: Math.round(parseFloat(base24kRate) * purityMultipliers[purity])
      }));
    }
  };

  // Handle manual purity rate change
  const handlePurityRateChange = (purity, value) => {
    setPurityRates(prev => ({
      ...prev,
      [purity]: value === '' ? '' : parseFloat(value) || 0
    }));
    // Unlink when manually edited
    setPurityRateLinks(prev => ({
      ...prev,
      [purity]: false
    }));
  };


  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile.includes(customerSearch)
  );

  const handleCustomerAdded = (newCustomer) => {
    setCustomers([...customers, newCustomer]);
    setSelectedCustomer(newCustomer.id);
    setCustomerSearch(newCustomer.name);
    toast.success('Customer added and selected!');
  };

  const addItem = (afterIndex = null) => {
    const newItemId = Date.now();
    const newItem = {
      id: newItemId,
      item_type: '',
      ready_item: false,
      tag_number: '',
      photos: [],
      weight_type: 'Fixed',
      weight_fixed: null,
      weight_min: null,
      weight_max: null,
      quantity: '1',
      purity: '22K',
      size: '',
      finish_color: 'Yellow Gold',
      making_type: 'PERCENT',
      making_value: '',
      vendor_short_code: '',
      public_remark: '',
      internal_remark: '',
      collapsed: false
    };
    
    if (afterIndex !== null) {
      // Insert after specific index
      const newItems = [...items];
      newItems.splice(afterIndex + 1, 0, newItem);
      setItems(newItems);
    } else {
      // Add to end
      setItems([...items, newItem]);
    }
    setExpandedItems({ ...expandedItems, [newItemId]: true });
  };

  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
    const newExpanded = { ...expandedItems };
    delete newExpanded[id];
    setExpandedItems(newExpanded);
  };

  const toggleItemExpand = (id) => {
    setExpandedItems({ ...expandedItems, [id]: !expandedItems[id] });
  };

  const collapseItem = (id) => {
    setExpandedItems({ ...expandedItems, [id]: false });
    const newItems = items.map(item =>
      item.id === id ? { ...item, collapsed: true } : item
    );
    setItems(newItems);
  };

  const toggleSection = (section) => {
    const isExpanding = !expandedSections[section];
    
    if (isExpanding) {
      // When expanding a section, collapse all previous sections
      const sectionOrder = ['items', 'rates', 'dates', 'payment'];
      const currentIndex = sectionOrder.indexOf(section);
      const newExpandedSections = { ...expandedSections };
      
      // Collapse all sections before the current one
      for (let i = 0; i < currentIndex; i++) {
        newExpandedSections[sectionOrder[i]] = false;
      }
      
      // Expand the current section
      newExpandedSections[section] = true;
      setExpandedSections(newExpandedSections);
    } else {
      // Just toggle if collapsing
      setExpandedSections({ ...expandedSections, [section]: !expandedSections[section] });
    }
  };

  const scrollToSection = (ref, section) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setExpandedSections({ ...expandedSections, [section]: true });
  };

  const updateItem = (id, field, value) => {
    const newItems = items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setItems(newItems);
  };

  const handlePhotoUpload = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Save form state BEFORE file selection (in case camera opens and page reloads)
    saveFormState();
    
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingImage({ itemId: id, imageData: reader.result });
      };
      reader.readAsDataURL(file);
    }
    // Reset the input to allow selecting the same file again
    e.target.value = '';
  };

  const handleImageSaved = (itemId, editedImage) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      updateItem(itemId, 'photos', [...item.photos, editedImage]);
      toast.success('Photo added successfully');
    }
  };

  const removePhoto = (itemId, photoIndex) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      const newPhotos = item.photos.filter((_, i) => i !== photoIndex);
      updateItem(itemId, 'photos', newPhotos);
    }
  };

  const editPhoto = (itemId, photoIndex) => {
    const item = items.find(i => i.id === itemId);
    if (item && item.photos[photoIndex]) {
      setEditingImage({ itemId, photoIndex, imageData: item.photos[photoIndex] });
    }
  };

  const handleImageEdited = (itemId, photoIndex, editedImage) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      const newPhotos = [...item.photos];
      newPhotos[photoIndex] = editedImage;
      updateItem(itemId, 'photos', newPhotos);
      toast.success('Photo updated successfully');
    }
  };

  const toggleInternalRemark = (id) => {
    setShowInternalRemark({ ...showInternalRemark, [id]: !showInternalRemark[id] });
  };

  const getSizePresets = (itemType) => {
    // Handle both old (item_type + presets) and new (preset_name + item_types + sizes) structures
    const matchingPresets = sizePresets.filter(p => {
      // New structure: check if itemType is in item_types array
      if (p.item_types && Array.isArray(p.item_types)) {
        return p.item_types.includes(itemType);
      }
      // Old structure: check if item_type matches
      if (p.item_type === itemType) {
        return true;
      }
      return false;
    });
    
    console.log(`Size presets for ${itemType}:`, matchingPresets);
    
    // Combine all sizes from matching presets
    const allSizes = [];
    matchingPresets.forEach(preset => {
      const sizes = preset.sizes || preset.presets || [];
      allSizes.push(...sizes);
    });
    
    // Remove duplicates and return
    const uniqueSizes = [...new Set(allSizes)];
    console.log(`Final sizes for ${itemType}:`, uniqueSizes);
    return uniqueSizes;
  };

  const getMakingPresets = (purity, calculationType) => {
    // Find presets matching BOTH purity AND calculation type (strict matching)
    const matchingPresets = makingPresets.filter(p => {
      // Must match purity
      if (p.purity !== purity) return false;
      
      // Must have calculation_type AND it must match exactly
      if (!p.calculation_type) return false;
      
      return p.calculation_type === calculationType;
    });
    
    console.log(`Making presets for ${purity} + ${calculationType}:`, matchingPresets);
    
    // Get first matching preset's values
    if (matchingPresets.length > 0) {
      const presetValues = matchingPresets[0].presets || [];
      console.log(`Returning preset values:`, presetValues);
      return presetValues;
    }
    
    console.log(`No making presets found for ${purity} + ${calculationType}`);
    return [];
  };

  // Payment handlers
  const handlePaymentSave = (paymentData) => {
    // Ensure mode field exists
    if (!paymentData.mode) {
      console.error('Payment data missing mode field:', paymentData);
      toast.error('Payment mode is required');
      return;
    }

    if (editingPaymentIndex !== null) {
      // Edit mode - update existing payment
      const oldPayment = payments[editingPaymentIndex];
      
      // Adjust metal credit balance
      if (oldPayment.mode === 'Metal Credit' && oldPayment.fine_wt) {
        setMetalCreditBalance(prev => Math.max(0, prev - parseFloat(oldPayment.fine_wt)));
      }
      if (paymentData.mode === 'Metal Credit' && paymentData.fine_wt) {
        setMetalCreditBalance(prev => prev + parseFloat(paymentData.fine_wt));
      }
      
      // Adjust old gold balance
      if (oldPayment.mode === 'Old Gold' && oldPayment.fine_grams) {
        setMetalCreditBalance(prev => Math.max(0, prev - parseFloat(oldPayment.fine_grams)));
      }
      if (paymentData.mode === 'Old Gold' && paymentData.fine_grams) {
        setMetalCreditBalance(prev => prev + parseFloat(paymentData.fine_grams));
      }
      
      const updatedPayments = [...payments];
      updatedPayments[editingPaymentIndex] = {
        ...oldPayment,
        ...paymentData
      };
      setPayments(updatedPayments);
      toast.success('Payment updated successfully');
      setEditingPaymentIndex(null);
    } else {
      // Add mode - create new payment
      const newPayment = {
        payment_id: Date.now().toString(),
        ...paymentData
      };
      
      console.log('Adding payment with data:', newPayment);
      
      setPayments([...payments, newPayment]);
      
      // Update metal credit balance if it's a metal credit entry
      if (paymentData.mode === 'Metal Credit' && paymentData.fine_wt) {
        setMetalCreditBalance(prev => prev + parseFloat(paymentData.fine_wt));
      }
      
      // Update metal credit balance if it's old gold
      if (paymentData.mode === 'Old Gold' && paymentData.fine_grams) {
        setMetalCreditBalance(prev => prev + parseFloat(paymentData.fine_grams));
      }
      
      toast.success('Payment added successfully');
    }
  };

  const handlePaymentEdit = (payment, idx) => {
    setEditingPaymentIndex(idx);
    
    // Determine which modal to open based on payment mode
    const modalMap = {
      'Cash': 'cash',
      'Card': 'card',
      'UPI': 'upi',
      'IMPS': 'bank',
      'NEFT/RTGS': 'bank',
      'Cheque': 'bank',
      'Old Gold': 'old-gold',
      'Metal Credit': 'metal'
    };
    
    setActivePaymentModal(modalMap[payment.mode] || null);
  };

  const handlePaymentDelete = (idx) => {
    setDeletePaymentIndex(idx);
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = () => {
    if (deletePaymentIndex === null) return;
    
    const payment = payments[deletePaymentIndex];
    
    // Update metal credit balance if deleting a metal credit entry
    if (payment.mode === 'Metal Credit' && payment.fine_wt) {
      setMetalCreditBalance(prev => Math.max(0, prev - parseFloat(payment.fine_wt)));
    }
    
    // Update metal credit balance if deleting old gold
    if (payment.mode === 'Old Gold' && payment.fine_grams) {
      setMetalCreditBalance(prev => Math.max(0, prev - parseFloat(payment.fine_grams)));
    }
    
    setPayments(payments.filter((_, i) => i !== deletePaymentIndex));
    toast.success('Payment deleted');
    setShowDeleteConfirm(false);
    setDeletePaymentIndex(null);
  };
  
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletePaymentIndex(null);
  };

  // Order Date handlers
  const handleOrderDateChange = (newDate) => {
    // Allow all users to set any date - admin will review via approval page
    setOrderDate(newDate);
  };

  // OTP handlers removed - backdating allowed without OTP

  const calculateTotals = () => {
    try {
    let subtotal = 0;
    
    items.forEach(item => {
      const weight = item.weight_type === 'Fixed' 
        ? parseFloat(item.weight_fixed || 0) 
        : (parseFloat(item.weight_min || 0) + parseFloat(item.weight_max || 0)) / 2;
      
      let rate = rateMode === 'single' 
        ? (parseFloat((purityRates || {})[item.purity]) || parseFloat(base24kRate) || 0)
        : parseFloat((itemRates || {})[item.id] || 0);
      
      // Apply GST only for TAXABLE orders with EXCL input style
      if (taxScope !== 'KACCHA' && gstInputStyle === 'EXCL' && rate > 0) {
        rate = rate * 1.03; // Add 3% GST
      }
      
      const making = parseFloat(item.making_value || 0);
      
      // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
      let itemTotal;
      if (item.making_type === 'WASTAGE') {
        // Wastage/Tonnage: item_total = weight × 24K rate × (tunch / 100)
        let rate24k = parseFloat(base24kRate) || 0;
        if (taxScope !== 'KACCHA' && gstInputStyle === 'EXCL' && rate24k > 0) {
          rate24k = rate24k * 1.03;
        }
        itemTotal = weight * rate24k * (making / 100);
      } else {
        itemTotal = weight * rate;
        // Add making charges based on type
        if (item.making_type === 'PERCENT') {
          itemTotal += itemTotal * (making / 100);
        } else if (item.making_type === 'PER_GRAM') {
          itemTotal += weight * making;
        } else if (item.making_type === 'FLAT') {
          itemTotal += making;
        }
      }
      
      subtotal += itemTotal;
    });

    // Calculate cash advance paid (subtract refunds)
    const advancePaid = (payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') return sum; // Skip metal credit from cash advance
      const amount = parseFloat(p.amount) || 0;
      // Subtract if it's a refund, add if it's a payment
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);
    
    // Calculate metal credit advance equivalent
    const metalAdvance = (payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit' && p.advance_equivalent) {
        return sum + parseFloat(p.advance_equivalent);
      }
      return sum;
    }, 0);
    
    const totalAdvance = advancePaid + metalAdvance;
    const balance = subtotal - advancePaid; // Balance is only against cash payments
    const advancePercent = subtotal > 0 ? (totalAdvance / subtotal * 100) : 0;

    return { subtotal, advancePaid, metalAdvance, totalAdvance, balance, advancePercent };
    } catch (e) {
      console.error('calculateTotals error:', e);
      return { subtotal: 0, advancePaid: 0, metalAdvance: 0, totalAdvance: 0, balance: 0, advancePercent: 0 };
    }
  };

  const getSectionStatus = (section) => {
    switch (section) {
      case 'items':
        return items.length > 0 ? 'done' : 'pending';
      case 'rates':
        // Only validate based on current mode
        if (rateMode === 'single') {
          // Different validation based on rate policy
          if (ratePolicy === 'lock-today') {
            // Lock Rate Today: Base rate is mandatory
            return base24kRate && parseFloat(base24kRate) > 0 ? 'done' : 'pending';
          } else if (ratePolicy === 'rate-at-delivery') {
            // Rate at Delivery: Just need GST input style selected
            return gstInputStyle ? 'done' : 'pending';
          } else {
            // None or no policy: Always pass
            return 'done';
          }
        } else {
          // Per item - only validate items that exist
          return items.every(item => itemRates[item.id] && parseFloat(itemRates[item.id]) > 0) ? 'done' : 'pending';
        }
      case 'dates': {
        // Ready items don't need delivery dates
        const nonReadyItems = items.filter(item => !item.ready_item);
        
        if (nonReadyItems.length === 0) {
          // All items are ready, no delivery date needed
          return 'done';
        }
        
        if (deliveryMode === 'single') {
          return singleDeliveryDate ? 'done' : 'pending';
        } else {
          // Per item - only check non-ready items
          return nonReadyItems.every(item => itemDeliveryDates[item.id]) ? 'done' : 'pending';
        }
      }
      case 'payment': {
        const { subtotal, totalAdvance } = calculateTotals();
        const minRequired = subtotal * 0.8;
        return totalAdvance >= minRequired ? 'done' : totalAdvance > 0 ? 'warning' : 'pending';
      }
      case 'review':
        return getSectionStatus('items') === 'done' && 
               getSectionStatus('rates') === 'done' && 
               getSectionStatus('dates') === 'done' ? 'done' : 'pending';
      default:
        return 'pending';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done':
        return <Check className="w-3 h-3 text-green-600" />;
      case 'warning':
        return <AlertCircle className="w-3 h-3 text-orange-500" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  };

  const handleFinalizeOrder = () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    if (items.length === 0) {
      toast.error('Please add at least one item');
      scrollToSection(itemsRef, 'items');
      return;
    }

    if (getSectionStatus('rates') !== 'done') {
      toast.error('Please complete rates section');
      scrollToSection(ratesRef, 'rates');
      return;
    }

    // Enhanced delivery date validation - skip ready items
    const nonReadyItems = items.filter(item => !item.ready_item);
    
    if (nonReadyItems.length > 0) {
      if (deliveryMode === 'single') {
        if (!singleDeliveryDate) {
          toast.error('Please set delivery date');
          scrollToSection(datesRef, 'dates');
          return;
        }
      } else {
        // Per-item mode - check only non-ready items have dates
        const missingDates = nonReadyItems.filter(item => !itemDeliveryDates[item.id]);
        if (missingDates.length > 0) {
          toast.error(`${missingDates.length} item(s) missing delivery date`);
          scrollToSection(datesRef, 'dates');
          return;
        }
      }
    }

    // Submit directly with inline signatures (no modal)
    handleSignatureConfirm({
      management_signature_name: managementSignatureName,
      management_signature: managementSignature,
      customer_signature: customerSignature
    });
  };

  const handleSignatureConfirm = async (signatureData) => {
    setShowSignatureModal(false);
    setLoading(true);
    setIsSubmitting(true);  // Allow navigation after submit
    
    try {
      // Collect all base64 images for batch upload
      toast.info('Uploading images...', { duration: 2000 });
      
      // Collect all photos that need uploading
      const allBase64Photos = [];
      const photoMapping = []; // Track which item and index each photo belongs to
      
      items.forEach((item, itemIndex) => {
        if (item.photos && item.photos.length > 0) {
          item.photos.forEach((photo, photoIndex) => {
            if (isBase64DataUrl(photo)) {
              allBase64Photos.push(photo);
              photoMapping.push({ itemIndex, photoIndex, type: 'itemPhoto' });
            }
          });
        }
      });
      
      // Add signatures to batch if they're base64
      if (signatureData.management_signature && isBase64DataUrl(signatureData.management_signature)) {
        allBase64Photos.push(signatureData.management_signature);
        photoMapping.push({ type: 'managementSig' });
      }
      
      if (signatureData.customer_signature && isBase64DataUrl(signatureData.customer_signature)) {
        allBase64Photos.push(signatureData.customer_signature);
        photoMapping.push({ type: 'customerSig' });
      }
      
      // Upload all photos in parallel with single signature request
      let uploadedUrls = [];
      if (allBase64Photos.length > 0) {
        try {
          const results = await uploadMultipleToCloudinary(allBase64Photos, 'orders');
          uploadedUrls = results.map(r => r.url);
        } catch (e) {
          console.error('Batch upload failed, continuing without images:', e);
          toast.warning('Some images may not have uploaded. Order will be created without them.');
          // Continue without the photos rather than failing
          uploadedUrls = allBase64Photos.map(() => null);
        }
      }
      
      // Build URL map from uploaded results
      let uploadedManagementSig = signatureData.management_signature;
      let uploadedCustomerSig = signatureData.customer_signature;
      const itemPhotoUrls = items.map(item => 
        item.photos ? [...item.photos] : []
      );
      
      // Map uploaded URLs back to their sources
      photoMapping.forEach((mapping, idx) => {
        const url = uploadedUrls[idx];
        if (mapping.type === 'itemPhoto') {
          itemPhotoUrls[mapping.itemIndex][mapping.photoIndex] = url;
        } else if (mapping.type === 'managementSig') {
          uploadedManagementSig = url;
        } else if (mapping.type === 'customerSig') {
          uploadedCustomerSig = url;
        }
      });
      
      // Process items with uploaded photo URLs
      const processedItems = items.map((item, itemIndex) => {
        const { id, collapsed, ...itemData } = item;
        
        return {
          ...itemData,
          photos: itemPhotoUrls[itemIndex] || [],
          ready_item: item.ready_item || false,
          tag_number: item.tag_number || null,
          weight_fixed: item.weight_type === 'Fixed' ? parseFloat(item.weight_fixed) : null,
          weight_min: item.weight_type === 'Range' ? parseFloat(item.weight_min) : null,
          weight_max: item.weight_type === 'Range' ? parseFloat(item.weight_max) : null,
          making_value: item.making_value ? parseFloat(item.making_value) : null,
          rate: rateMode === 'single' 
            ? (parseFloat(purityRates[item.purity]) || parseFloat(base24kRate) || 0)
            : parseFloat(itemRates[item.id] || 0),
          delivery_date: item.ready_item 
            ? orderDate 
            : (deliveryMode === 'single' ? singleDeliveryDate : itemDeliveryDates[item.id]),
          rate_policy: ratePolicy,
          status: item.ready_item ? 'READY' : 'Unassigned'
        };
      });
      
      // In edit mode, start with existing order data to preserve all fields
      const baseData = editMode && existingOrder ? { ...existingOrder } : {};
      
      // Convert itemRates to item_rates for backend (per-item mode)
      let finalItemRates = {};
      let finalPurityRates = {};
      
      if (rateMode === 'per-item' && Object.keys(itemRates).length > 0) {
        // Map itemRates to item indices for persistence
        items.forEach((item, index) => {
          if (itemRates[item.id]) {
            finalItemRates[`item_${index}`] = parseFloat(itemRates[item.id]);
          }
        });
        
        // Also populate purity_rates for backward compatibility
        items.forEach(item => {
          if (itemRates[item.id]) {
            const purity = item.purity || '24K';
            finalPurityRates[purity] = parseFloat(itemRates[item.id]);
          }
        });
      } else {
        // Single rate mode - use purity_rates
        finalPurityRates = purityRates;
      }

      const orderData = {
        ...baseData, // Preserve all existing fields
        customer_id: selectedCustomer,
        
        // Order date (supports backdating)
        order_date: orderDate,
        
        // Rate settings
        rate_mode: rateMode,
        rate_policy: ratePolicy,
        lock_date: lockDate || existingOrder?.lock_date || null,
        
        // Tax and GST settings
        tax_scope: taxScope,
        gst_applicable: gstApplicable,
        gst_input_style: gstInputStyle,
        gst_percent_snapshot: gstPercent,
        
        // Rate data
        base24k_rate_ex_gst: base24kRate ? parseFloat(base24kRate) : null,
        purity_rates: finalPurityRates,
        item_rates: finalItemRates,  // Added: per-item rates
        
        // Items with Cloudinary URLs
        items: processedItems,
        
        // Legacy fields for backward compatibility
        single_rate: rateMode === 'single' ? parseFloat(base24kRate) : null,
        single_delivery_date: deliveryMode === 'single' ? singleDeliveryDate : null,
        
        // Process payments - upload Metal Credit deposit photos to Cloudinary
        advance_payments: await Promise.all(payments.map(async (p) => {
          // Ensure mode field exists
          if (!p.mode) {
            console.error('Payment missing mode:', p);
            throw new Error('Payment mode is required');
          }
          
          const basePayment = {
            mode: p.mode,
            amount: parseFloat(p.amount) || 0,
            date: p.date || orderDate || new Date().toISOString().split('T')[0],
            note: p.note || null,
            transaction_type: p.transaction_type || 'PAYMENT',
            direction: p.direction || 'IN',
            payment_id: p.payment_id || null,
            timestamp: p.timestamp || new Date().toISOString()
          };
          
          // Include all Metal Credit specific fields
          if (p.mode === 'Metal Credit') {
            // Process deposit photos
            let processedDeposits = [];
            if (p.deposits && p.deposits.length > 0) {
              processedDeposits = await Promise.all(p.deposits.map(async (deposit) => {
                let uploadedPhotos = [];
                if (deposit.photos && deposit.photos.length > 0) {
                  uploadedPhotos = await Promise.all(deposit.photos.map(async (photo) => {
                    // Photo structure: { original, edited, timestamp }
                    const imageToUpload = photo.edited || photo.original || photo;
                    if (isBase64DataUrl(imageToUpload)) {
                      try {
                        const result = await uploadToCloudinary(imageToUpload, 'orders');
                        return { ...photo, edited: result.url, original: result.url };
                      } catch (e) {
                        console.error('Failed to upload deposit photo:', e);
                        return photo;
                      }
                    }
                    return photo;
                  }));
                }
                return { ...deposit, photos: uploadedPhotos };
              }));
            }
            
            return {
              ...basePayment,
              fine_wt: p.fine_wt,
              advance_equivalent: p.advance_equivalent,
              deposits: processedDeposits,
              metal_type: p.metal_type,
              purity: p.purity,
              net_wt: p.net_wt,
              reference: p.reference
            };
          }
          
          // Include all Old Gold specific fields
          if (p.mode === 'Old Gold') {
            return {
              ...basePayment,
              fine_grams: p.fine_grams,
              items: p.items || []
            };
          }
          
          // Include all Bank Transfer specific fields
          if (p.mode === 'IMPS' || p.mode === 'NEFT/RTGS') {
            return {
              ...basePayment,
              utr_ref: p.utr_ref,
              bank_name: p.bank_name,
              payer_acct_last4: p.payer_acct_last4
            };
          }
          
          // Include all Cheque specific fields
          if (p.mode === 'Cheque') {
            return {
              ...basePayment,
              cheque_no: p.cheque_no,
              cheque_bank: p.cheque_bank,
              cheque_date: p.cheque_date,
              cheque_status: p.cheque_status
            };
          }
          
          // Include all Card specific fields
          if (p.mode === 'Card') {
            return {
              ...basePayment,
              card_last4: p.card_last4,
              pos_ref: p.pos_ref
            };
          }
          
          // Include all UPI specific fields
          if (p.mode === 'UPI') {
            return {
              ...basePayment,
              upi_ref: p.upi_ref,
              upi_handle: p.upi_handle
            };
          }
          
          // Default return for other payment types (Cash, etc.)
          return basePayment;
        })),
        
        // Signatures - use uploaded URLs
        management_signature_name: signatureData.management_signature_name,
        management_signature: uploadedManagementSig,
        customer_signature: uploadedCustomerSig,
        
        // Payment reminder date (extract date part from datetime)
        reminder_date: paymentReminderData && paymentReminderData.enabled && paymentReminderData.due_at 
          ? paymentReminderData.due_at.split('T')[0]
          : (existingOrder?.reminder_date || null),
        
        // Send full payment reminder data to backend
        // Send enabled: false when reminder is disabled to trigger deletion
        payment_reminder_data: paymentReminderData 
          ? (paymentReminderData.enabled 
              ? {
                  enabled: true,
                  due_at: paymentReminderData.due_at,
                  amount_due: paymentReminderData.amount_due,
                  channels: paymentReminderData.channels || [],
                  reason_tags: paymentReminderData.reason_tags || [],
                  notes: paymentReminderData.notes || '',
                  auto_snooze: paymentReminderData.auto_snooze || null
                }
              : { enabled: false }
            )
          : null,
        
        // Preserve reminder enabled status from existing order
        reminder_enabled: paymentReminderData?.enabled ?? existingOrder?.reminder_enabled ?? false,
      };

      console.log('Sending order data:', orderData);
      console.log('Payment Reminder Data:', paymentReminderData);
      console.log('Extracted reminder_date:', orderData.reminder_date);
      
      let res;
      if (editMode && existingOrder) {
        // Update existing order
        res = await axiosInstance.put(`/orders/${existingOrder.order_id}`, orderData);
        
        // Check if changes require approval (staff editing approved order)
        if (res.data.requires_approval) {
          clearSavedState(); // Clear saved state on successful submission
          toast.success('✓ Changes submitted for admin approval', {
            duration: 5000,
            description: 'An admin will review your changes before they are applied.'
          });
          navigate('/my-submissions');
        } else {
          clearSavedState(); // Clear saved state on successful submission
          toast.success('Order updated successfully!');
          navigate(`/orders/${existingOrder.order_id}`);
        }
      } else {
        // Create new order
        res = await axiosInstance.post('/orders', orderData);
        
        clearSavedState(); // Clear saved state on successful submission
        
        // All users: Redirect to order detail page
        toast.success('✓ Order created successfully!', {
          duration: 3000,
          description: user?.role !== 'Admin' && user?.role !== 'Manager' 
            ? 'Order submitted for admin approval. You can view, edit, or print your order.'
            : 'You can view, edit, or print your order.'
        });
        navigate(`/orders/${res.data.order_id}`);
      }
      
      // Store order data and show success modal
      
      // Clear saved form state on successful submission
      clearSavedState();
      
      setCreatedOrderData({
        order_id: res.data.order_id,
        customer_name: customers.find(c => c.customer_id === selectedCustomer)?.name || 'Customer'
      });
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Order creation error:', err);
      
      // Reset loading states immediately
      setLoading(false);
      setIsSubmitting(false);
      
      // Show appropriate error message
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          const errors = err.response.data.detail.map(e => `${e.loc?.join('.') || 'field'}: ${e.msg}`).join(', ');
          toast.error(`Validation errors: ${errors}`);
        } else {
          toast.error(err.response.data.detail);
        }
      } else if (err.message) {
        toast.error(`Error: ${err.message}`);
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    // Navigation already handled in handleSubmit - just close modal
  };

  const { subtotal, advancePaid, metalAdvance, totalAdvance, balance, advancePercent } = calculateTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-32">
      {/* Full-screen Loading Overlay during Order Creation */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-200 border-t-amber-600 mx-auto mb-4"></div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Creating Order...</h3>
            <p className="text-gray-600 text-sm">Please wait while we upload images and create your order.</p>
            <p className="text-xs text-gray-400 mt-3">This may take a few seconds</p>
          </div>
        </div>
      )}
      
      {/* Sticky Header with Stepper */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-200">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <button 
              type="button"
              onClick={() => handleConfirmExit(() => navigate(-1))} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
              title="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-bold text-gray-800">
              {editMode ? `Edit Order - ${existingOrder?.order_id}` : 'Create Order'}
            </h1>
          </div>
          
          {/* Mini Stepper */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            <button
              type="button"
              onClick={() => scrollToSection(itemsRef, 'items')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.items ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
              data-testid="stepper-items"
            >
              <span>Items ({items.length})</span>
              {getStatusIcon(getSectionStatus('items'))}
            </button>
            
            <button
              type="button"
              onClick={() => scrollToSection(ratesRef, 'rates')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.rates ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
              data-testid="stepper-rates"
            >
              <span>Rates</span>
              {getStatusIcon(getSectionStatus('rates'))}
            </button>
            
            <button
              type="button"
              onClick={() => scrollToSection(datesRef, 'dates')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.dates ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
              data-testid="stepper-dates"
            >
              <span>Dates</span>
              {getStatusIcon(getSectionStatus('dates'))}
            </button>
            
            <button
              type="button"
              onClick={() => scrollToSection(paymentRef, 'payment')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.payment ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
              data-testid="stepper-payment"
            >
              <span>Payment</span>
              {getStatusIcon(getSectionStatus('payment'))}
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {/* Merged Order Header - Customer & Order Date */}
        <div className="premium-card border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Order Date - Left/Top */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Order Date *</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className="input-field text-sm flex-1"
                    value={orderDate}
                    onChange={(e) => handleOrderDateChange(e.target.value)}
                  />
                  {orderDate < new Date().toISOString().split('T')[0] && (
                    <span className="px-2 py-1 bg-amber-200 text-amber-800 text-xs font-semibold rounded whitespace-nowrap">
                      Back-dated
                    </span>
                  )}
                </div>
              </div>
              
              {/* Customer Selector - Right/Bottom */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Customer *</label>
                {selectedCustomer ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white border-2 border-green-200 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-gray-800">
                            {customers.find(c => c.id === selectedCustomer)?.name || 'Selected'}
                          </span>
                          {customers.find(c => c.id === selectedCustomer)?.mobile && (
                            <span className="text-xs text-gray-600 ml-2">
                              • {customers.find(c => c.id === selectedCustomer)?.mobile}
                            </span>
                          )}
                          {customers.find(c => c.id === selectedCustomer)?.alternate_mobile && (
                            <span className="text-xs text-gray-500 ml-1">
                              / {customers.find(c => c.id === selectedCustomer)?.alternate_mobile}
                            </span>
                          )}
                          {customers.find(c => c.id === selectedCustomer)?.email && (
                            <div className="text-xs text-gray-600 mt-1 break-all">
                              ✉️ {customers.find(c => c.id === selectedCustomer)?.email}
                            </div>
                          )}
                          {customers.find(c => c.id === selectedCustomer)?.address && (
                            <div className="text-xs text-gray-600 mt-1 break-words">
                              📍 {customers.find(c => c.id === selectedCustomer)?.address}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const customer = customers.find(c => c.id === selectedCustomer);
                              setEditingCustomer(customer);
                              setShowEditCustomerModal(true);
                            }}
                            className="text-xs text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-1"
                          >
                            <Edit3 className="w-3 h-3" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowCustomerModal(true)}
                            className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                          >
                            Change
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowCustomerModal(true)}
                      className="input-field w-full text-sm text-left text-gray-500 hover:bg-amber-50 transition-colors"
                    >
                      Search customer...
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomerModal(true)}
                      className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                    >
                      + Add New Customer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 1: ORDER ITEMS */}
        <div ref={itemsRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('items')}
          >
            <div className="flex items-center gap-3">
              <h2 className="text-base font-bold text-gray-800">Order Items</h2>
              <span className="bg-white text-amber-600 font-bold text-xs px-2 py-1 rounded-full">{items.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  addItem();
                }}
                className="text-xs font-semibold text-amber-700 hover:text-amber-900 flex items-center gap-1 px-2 py-1 bg-white rounded-lg"
                data-testid="add-item-header"
              >
                <Plus className="w-3 h-3" />
                Add Item
              </button>
              {expandedSections.items ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>

          {expandedSections.items && (
            <div className="p-4 space-y-3">
              {items.length === 0 ? (
                <div className="text-center py-8 bg-amber-50 rounded-xl">
                  <p className="text-sm text-gray-600">No items yet. Click &quot;+ Add Item&quot; above</p>
                </div>
              ) : (
                items.map((item, itemIndex) => (
                  <div key={item.id} className="space-y-2">
                    <div className="border-2 border-amber-200 rounded-xl overflow-hidden bg-white">
                      {/* Collapsed Item Preview */}
                      {!expandedItems[item.id] ? (
                        <div className={`p-3 ${item.isLocked ? 'bg-gray-100' : 'bg-amber-50'}`}>
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${item.isLocked ? 'bg-gray-500 text-white' : 'bg-amber-600 text-white'}`}>#{itemIndex + 1}</span>
                              {item.isLocked && (
                                <div className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold">
                                  <Lock className="w-3 h-3" />
                                  In Production
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleItemExpand(item.id)}
                                className="p-1 hover:bg-amber-100 rounded"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                              {!item.isLocked && (
                                <button
                                  type="button"
                                  onClick={() => removeItem(item.id)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap text-xs leading-relaxed">
                              {item.ready_item && (
                                <>
                                  <span className="bg-green-500 text-white px-2 py-0.5 rounded font-semibold">Ready</span>
                                  <span className="text-gray-500">•</span>
                                </>
                              )}
                              {item.isLocked && (
                                <>
                                  <span className="bg-gray-500 text-white px-2 py-0.5 rounded font-semibold">🔒 Locked</span>
                                  <span className="text-gray-500">•</span>
                                </>
                              )}
                              <span className="font-semibold text-gray-900">{item.item_type || 'Select Item Type'}</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-700">
                                {item.weight_type === 'Fixed' ? `${item.weight_fixed || 0}g` : `${item.weight_min || 0}–${item.weight_max || 0}g`}
                              </span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-700">Qty {item.quantity} pc</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-700">{item.purity}</span>
                              {item.size && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">Sz {item.size}</span>
                                </>
                              )}
                              {item.making_value && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">
                                    Mkg {item.making_value}{item.making_type === 'PERCENT' ? '%' : item.making_type === 'PER_GRAM' ? '₹/g' : item.making_type === 'WASTAGE' ? ' %Wst' : '₹'}
                                  </span>
                                </>
                              )}
                              {item.finish_color && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">{item.finish_color}</span>
                                </>
                              )}
                              {item.vendor_short_code && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">V:{item.vendor_short_code}</span>
                                </>
                              )}
                              {item.ready_item && item.tag_number && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">Tag:{item.tag_number}</span>
                                </>
                              )}
                              {item.public_remark && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-600 italic">
                                    {item.public_remark.length > 30 ? `${item.public_remark.substring(0, 30)}...` : item.public_remark}
                                  </span>
                                </>
                              )}
                              {item.photos && item.photos.length > 0 && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-700">{item.photos.length} 📷</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Expanded Item Header */}
                          <div className={`p-3 ${item.isLocked ? 'bg-gray-100' : 'bg-amber-50'} flex justify-between items-center`}>
                            <div className="flex items-center gap-2 flex-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${item.isLocked ? 'bg-gray-500 text-white' : 'bg-amber-600 text-white'}`}>#{itemIndex + 1}</span>
                              {item.isLocked && (
                                <div className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">
                                  <Lock className="w-3 h-3" />
                                  In Production - Cannot Edit
                                </div>
                              )}
                              <div>
                                <h4 className="font-semibold text-sm">{item.item_type || 'Select Item Type'}</h4>
                                <p className="text-xs text-gray-600">
                                  {item.weight_type === 'Fixed' ? `${item.weight_fixed || 0}g` : `${item.weight_min || 0}-${item.weight_max || 0}g`} | {item.purity}
                                  {item.productionStatus && <span className="ml-2 text-blue-600 font-semibold">Status: {item.productionStatus}</span>}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleItemExpand(item.id)}
                                className="p-1"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              {!item.isLocked && (
                                <button
                                  type="button"
                                  onClick={() => removeItem(item.id)}
                                  className="p-1 text-red-500"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Expanded Item Form */}
                          <div className="p-3 space-y-3 text-sm">
                            {/* Lock Warning Banner */}
                            {item.isLocked && (
                              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 flex items-start gap-2">
                                <Lock className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-sm font-bold text-red-700">🔒 Item Assigned to Vendor - Locked</p>
                                  <p className="text-xs text-red-600 mt-1">
                                    Status: {item.productionStatus || 'Assigned'} {item.vendorCode ? `- Vendor: ${item.vendorCode}` : ''} - Cannot edit after vendor assignment
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Item Type + Ready Item Checkbox */}
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-semibold text-gray-700">Item Type</label>
                                <label 
                                  htmlFor={`ready-${item.id}`} 
                                  className="flex items-center gap-1.5 cursor-pointer select-none hover:bg-amber-50 px-2 py-1 rounded transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    id={`ready-${item.id}`}
                                    checked={item.ready_item || false}
                                    disabled={item.isLocked}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      const newValue = e.target.checked;
                                      
                                      // Update both ready_item and weight_type in one go
                                      const newItems = items.map(i =>
                                        i.id === item.id 
                                          ? { ...i, ready_item: newValue, weight_type: newValue ? 'Fixed' : i.weight_type }
                                          : i
                                      );
                                      setItems(newItems);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-4 h-4 text-amber-500 border-2 border-gray-300 rounded cursor-pointer focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ accentColor: '#d4af37' }}
                                  />
                                  <span className="text-xs font-semibold text-gray-700 select-none">Ready Item</span>
                                </label>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!item.isLocked) {
                                    setItemTypeModalTargetId(item.id);
                                    setShowItemTypeModal(true);
                                  }
                                }}
                                disabled={item.isLocked}
                                className={`input-field w-full text-sm text-left flex items-center justify-between transition-colors ${item.isLocked ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:bg-amber-50'}`}
                              >
                                <span>{item.item_type || 'Select Item Type'}</span>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </button>
                            </div>
                            
                            {/* Weight Type - Hidden for Ready Items */}
                            {!item.ready_item && (
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Weight Type</label>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => !item.isLocked && updateItem(item.id, 'weight_type', 'Fixed')}
                                    disabled={item.isLocked}
                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                                      item.weight_type === 'Fixed'
                                        ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                                        : item.isLocked
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    Fixed
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => !item.isLocked && updateItem(item.id, 'weight_type', 'Range')}
                                    disabled={item.isLocked}
                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                                      item.weight_type === 'Range'
                                        ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                                        : item.isLocked
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    Range
                                  </button>
                                </div>
                              </div>
                            )}

                        {/* Weight Input */}
                        {item.weight_type === 'Fixed' ? (
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Weight (g)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.weight_fixed || ''}
                              onChange={(e) => updateItem(item.id, 'weight_fixed', e.target.value)}
                              disabled={item.isLocked}
                              className={`input-field w-full text-sm ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                              placeholder="Fixed weight"
                            />
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Min (g)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.weight_min || ''}
                                onChange={(e) => updateItem(item.id, 'weight_min', e.target.value)}
                                disabled={item.isLocked}
                                className={`input-field w-full text-sm ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                placeholder="Min weight"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Max (g)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.weight_max || ''}
                                onChange={(e) => updateItem(item.id, 'weight_max', e.target.value)}
                                disabled={item.isLocked}
                                className={`input-field w-full text-sm ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                                placeholder="Max weight"
                              />
                            </div>
                          </div>
                        )}

                        {/* Tag Number - Only for Ready Items */}
                        {item.ready_item && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Tag Number</label>
                            <input
                              type="text"
                              className="input-field w-full text-sm"
                              value={item.tag_number || ''}
                              onChange={(e) => updateItem(item.id, 'tag_number', e.target.value)}
                              placeholder="Enter tag/serial number"
                            />
                          </div>
                        )}

                        {/* Quantity | Purity - With Modal Pickers */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Quantity</label>
                            <button
                              type="button"
                              onClick={() => {
                                if (!item.isLocked) {
                                  setModalItemId(item.id);
                                  setActiveModal('quantity');
                                }
                              }}
                              disabled={item.isLocked}
                              className={`input-field w-full text-sm text-left ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                            >
                              {item.quantity || '1'}
                            </button>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Purity</label>
                            <button
                              type="button"
                              onClick={() => {
                                if (!item.isLocked) {
                                  setModalItemId(item.id);
                                  setActiveModal('purity');
                                }
                              }}
                              disabled={item.isLocked}
                              className={`input-field w-full text-sm text-left ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                            >
                              {item.purity || '22K'}
                            </button>
                          </div>
                        </div>

                        {/* Size with Presets - Now visible for all items */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Size</label>
                          {getSizePresets(item.item_type).length > 0 && (
                            <div className="overflow-x-auto scrollbar-hide mb-2">
                              <div className="flex gap-1 pb-1" style={{ minWidth: 'min-content' }}>
                                {getSizePresets(item.item_type).map((preset) => (
                                  <button
                                    key={preset}
                                    type="button"
                                    onClick={() => !item.isLocked && updateItem(item.id, 'size', preset)}
                                    disabled={item.isLocked}
                                    className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                      item.size === preset
                                        ? 'bg-amber-500 text-white'
                                        : item.isLocked
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-amber-100 text-amber-700'
                                    }`}
                                  >
                                    {preset}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <input
                            type="text"
                            className={`input-field w-full text-sm ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                            value={item.size}
                            onChange={(e) => updateItem(item.id, 'size', e.target.value)}
                            disabled={item.isLocked}
                            placeholder="Enter size"
                          />
                        </div>

                        {/* Making */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Making</label>
                          <div className="flex gap-1 mb-2">
                            {['PERCENT', 'PER_GRAM', 'FLAT', 'WASTAGE'].map(type => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => !item.isLocked && updateItem(item.id, 'making_type', type)}
                                disabled={item.isLocked}
                                className={`flex-1 py-1.5 px-2 rounded text-xs font-semibold ${
                                  item.making_type === type
                                    ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                                    : item.isLocked
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {type === 'PERCENT' ? '%' : type === 'PER_GRAM' ? '₹/g' : type === 'FLAT' ? '₹' : 'T'}
                              </button>
                            ))}
                          </div>

                          {item.making_type === 'PERCENT' && getMakingPresets(item.purity, 'percentage').length > 0 && (
                            <div className="overflow-x-auto scrollbar-hide mb-2">
                              <div className="flex gap-1 pb-1" style={{ minWidth: 'min-content' }}>
                                {getMakingPresets(item.purity, 'percentage').map((preset) => (
                                  <button
                                    key={preset}
                                    type="button"
                                    onClick={() => !item.isLocked && updateItem(item.id, 'making_value', preset)}
                                    disabled={item.isLocked}
                                    className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                      item.making_value === preset
                                        ? 'bg-amber-500 text-white'
                                        : item.isLocked
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-amber-100 text-amber-700'
                                    }`}
                                  >
                                    {preset}%
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {item.making_type === 'PER_GRAM' && getMakingPresets(item.purity, 'per_gram').length > 0 && (
                            <div className="overflow-x-auto scrollbar-hide mb-2">
                              <div className="flex gap-1 pb-1" style={{ minWidth: 'min-content' }}>
                                {getMakingPresets(item.purity, 'per_gram').map((preset) => (
                                  <button
                                    key={preset}
                                    type="button"
                                    onClick={() => !item.isLocked && updateItem(item.id, 'making_value', preset)}
                                    disabled={item.isLocked}
                                    className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                      item.making_value === preset
                                        ? 'bg-green-500 text-white'
                                        : item.isLocked
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-green-100 text-green-700'
                                    }`}
                                  >
                                    ₹{preset}/g
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {item.making_type === 'FLAT' && getMakingPresets(item.purity, 'flat_rate').length > 0 && (
                            <div className="overflow-x-auto scrollbar-hide mb-2">
                              <div className="flex gap-1 pb-1" style={{ minWidth: 'min-content' }}>
                                {getMakingPresets(item.purity, 'flat_rate').map((preset) => (
                                  <button
                                    key={preset}
                                    type="button"
                                    onClick={() => !item.isLocked && updateItem(item.id, 'making_value', preset)}
                                    disabled={item.isLocked}
                                    className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                      item.making_value === preset
                                        ? 'bg-blue-500 text-white'
                                        : item.isLocked
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-blue-100 text-blue-700'
                                    }`}
                                  >
                                    ₹{preset}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {item.making_type === 'WASTAGE' && getMakingPresets(item.purity, 'wastage').length > 0 && (
                            <div className="overflow-x-auto scrollbar-hide mb-2">
                              <div className="flex gap-1 pb-1" style={{ minWidth: 'min-content' }}>
                                {getMakingPresets(item.purity, 'wastage').map((preset) => (
                                  <button
                                    key={preset}
                                    type="button"
                                    onClick={() => !item.isLocked && updateItem(item.id, 'making_value', preset)}
                                    disabled={item.isLocked}
                                    className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                                      item.making_value === preset
                                        ? 'bg-purple-500 text-white'
                                        : item.isLocked
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-purple-100 text-purple-700'
                                    }`}
                                  >
                                    {preset} %Wst
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <input
                            type="number"
                            step="0.01"
                            className={`input-field w-full text-sm ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                            value={item.making_value || ''}
                            onChange={(e) => updateItem(item.id, 'making_value', e.target.value)}
                            disabled={item.isLocked}
                            placeholder={
                              item.making_type === 'PERCENT' ? 'Enter %' :
                              item.making_type === 'PER_GRAM' ? 'Enter ₹/g' :
                              item.making_type === 'WASTAGE' ? 'Enter %Wst' : 'Enter ₹'
                            }
                          />
                        </div>

                        {/* Finish Color - Now visible for all items, Modal Picker */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Finish Color</label>
                          <button
                            type="button"
                            onClick={() => {
                              if (!item.isLocked) {
                                setModalItemId(item.id);
                                setActiveModal('finish');
                              }
                            }}
                            disabled={item.isLocked}
                            className={`input-field w-full text-sm text-left ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                          >
                            {item.finish_color || 'Select finish'}
                          </button>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Vendor Code</label>
                          <button
                            type="button"
                            onClick={() => {
                              if (!item.isLocked) {
                                setModalItemId(item.id);
                                setActiveModal('vendor');
                              }
                            }}
                            disabled={item.isLocked}
                            className={`input-field w-full text-sm text-left ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                          >
                            {item.vendor_short_code ? vendors.find(v => v.short_code === item.vendor_short_code)?.short_code || item.vendor_short_code : '-- No Vendor --'}
                          </button>
                        </div>

                        {/* Photos */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Photos</label>
                          <div className="flex gap-2">
                            {item.photos && item.photos.map((photo, i) => (
                              <div key={i} className="relative group">
                                <img src={photo} alt="Item" className="w-16 h-16 object-cover rounded border" />
                                {!item.isLocked && (
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(item.id, i)}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                            {(!item.photos || item.photos.length < 3) && !item.isLocked && (
                              <label 
                                htmlFor={`file-upload-${item.id}`}
                                className="w-16 h-16 border-2 border-dashed border-amber-300 rounded flex items-center justify-center cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  id={`file-upload-${item.id}`}
                                  type="file"
                                  multiple
                                  accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                                  onChange={(e) => handlePhotoUpload(item.id, e)}
                                  className="hidden"
                                  disabled={item.isLocked}
                                />
                                <Camera className="w-5 h-5 text-amber-600" />
                              </label>
                            )}
                          </div>
                        </div>

                        {/* Public Remark */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Public Remark</label>
                          <textarea
                            className={`input-field w-full text-sm ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
                            rows="2"
                            value={item.public_remark}
                            onChange={(e) => updateItem(item.id, 'public_remark', e.target.value)}
                            disabled={item.isLocked}
                          />
                        </div>

                        {/* Internal Remark */}
                        <div>
                          {!showInternalRemark[item.id] ? (
                            <button
                              type="button"
                              onClick={() => !item.isLocked && toggleInternalRemark(item.id)}
                              disabled={item.isLocked}
                              className={`text-xs font-semibold flex items-center gap-1 ${item.isLocked ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600'}`}
                            >
                              <Plus className="w-3 h-3" />
                              Add Internal Remark
                            </button>
                          ) : (
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Internal Remark</label>
                              <textarea
                                className={`input-field w-full text-sm ${item.isLocked ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-yellow-50'}`}
                                rows="2"
                                value={item.internal_remark}
                                onChange={(e) => updateItem(item.id, 'internal_remark', e.target.value)}
                                disabled={item.isLocked}
                              />
                            </div>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => collapseItem(item.id)}
                          className="btn-secondary w-full text-sm py-2"
                        >
                          Save & Collapse
                        </button>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Quick Add Button - Only show after LAST item */}
                {!expandedItems[item.id] && itemIndex === items.length - 1 && (
                  <button
                    type="button"
                    onClick={() => addItem()}
                    className="w-full py-2.5 text-xs font-semibold text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg flex items-center justify-center gap-1 transition-colors border-2 border-dashed border-amber-300"
                  >
                    <Plus className="w-4 h-4" />
                    Add Another Item
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>

        {/* SECTION 2: RATES */}
        <div ref={ratesRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('rates')}
          >
            <h2 className="text-base font-bold text-gray-800">Rates & Policy</h2>
            {expandedSections.rates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.rates && (
            <RatesSection
              taxScope={taxScope}
              setTaxScope={setTaxScope}
              gstApplicable={gstApplicable}
              setGstApplicable={setGstApplicable}
              gstInputStyle={gstInputStyle}
              setGstInputStyle={setGstInputStyle}
              gstPercent={gstPercent}
              rateMode={rateMode}
              setRateMode={setRateMode}
              base24kRate={base24kRate}
              handleBase24kChange={handleBase24kChange}
              purityRates={purityRates}
              handlePurityRateChange={handlePurityRateChange}
              purityRateLinks={purityRateLinks}
              togglePurityLink={togglePurityLink}
              items={items}
              itemRates={itemRates}
              setItemRates={setItemRates}
              ratePolicy={ratePolicy}
              setRatePolicy={setRatePolicy}
              lockDate={lockDate}
              setLockDate={setLockDate}
            />
          )}
        </div>

        {/* SECTION 3: DELIVERY DATES */}
        <div ref={datesRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('dates')}
          >
            <h2 className="text-base font-bold text-gray-800">Delivery Dates</h2>
            {expandedSections.dates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.dates && (
            <div className="p-4 space-y-4">
              {/* Delivery Mode Toggle */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (deliveryMode === 'per-item' && singleDeliveryDate) {
                      // Switching from Per Item to One Date - sync all items to single date
                      const newItemDates = {};
                      items.forEach(item => {
                        newItemDates[item.id] = singleDeliveryDate;
                      });
                      setItemDeliveryDates(newItemDates);
                    }
                    setDeliveryMode('single');
                  }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    deliveryMode === 'single'
                      ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  One Date
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryMode('per-item')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    deliveryMode === 'per-item'
                      ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Per Item
                </button>
              </div>

              {deliveryMode === 'single' ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Delivery Date *</label>
                    <input
                      type="date"
                      className="input-field w-full text-sm"
                      value={singleDeliveryDate}
                      min={orderDate}
                      onChange={(e) => setSingleDeliveryDate(e.target.value)}
                    />
                  </div>
                  
                  {/* Preset Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-xs text-gray-600 font-medium mr-2">Quick Select:</span>
                    {[7, 10, 14].map(days => (
                      <button
                        key={days}
                        type="button"
                        title={`Adds ${days} days from today`}
                        onClick={() => {
                          const baseDate = new Date(orderDate);
                          baseDate.setDate(baseDate.getDate() + days);
                          setSingleDeliveryDate(baseDate.toISOString().split('T')[0]);
                        }}
                        className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-200 transition-colors"
                      >
                        +{days}d
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Preset Buttons for Per Item mode */}
                  <div className="flex gap-2 flex-wrap items-center">
                    <span className="text-xs text-gray-600 font-medium">Quick Select:</span>
                    {[7, 10, 14].map(days => (
                      <button
                        key={days}
                        type="button"
                        title={`Adds ${days} days from today to all items`}
                        onClick={() => {
                          const baseDate = new Date(orderDate);
                          baseDate.setDate(baseDate.getDate() + days);
                          const dateStr = baseDate.toISOString().split('T')[0];
                          const newDates = {};
                          items.forEach(item => {
                            // Skip ready items
                            if (!item.ready_item) {
                              newDates[item.id] = dateStr;
                            }
                          });
                          setItemDeliveryDates({ ...itemDeliveryDates, ...newDates });
                        }}
                        className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-200 transition-colors"
                      >
                        +{days}d
                      </button>
                    ))}
                  </div>

                  {/* Per Item Date Pickers */}
                  <div className="space-y-2">
                    {items.map((item, idx) => {
                      const hasDate = itemDeliveryDates[item.id];
                      const isReady = item.ready_item;
                      const itemDesc = `#${idx + 1} • ${item.item_type} • ${
                        item.weight_type === 'Fixed' 
                          ? `${item.weight_fixed}g` 
                          : `${item.weight_min}-${item.weight_max}g`
                      } • ${item.purity} • Qty ${item.quantity || 1} pc`;
                      
                      return (
                        <div 
                          key={item.id} 
                          className={`border-2 rounded-lg p-3 transition-all ${
                            isReady 
                              ? 'border-green-200 bg-green-50/30' 
                              : hasDate 
                                ? 'border-green-200 bg-green-50/30' 
                                : 'border-amber-200 bg-amber-50/30'
                          }`}
                        >
                          {/* Mobile: Stack vertically */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <span className="text-xs text-gray-700 font-medium flex-1">
                              {isReady && <span className="bg-green-500 text-white px-2 py-0.5 rounded text-xs font-bold mr-2">Ready</span>}
                              {itemDesc}
                            </span>
                            {isReady ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-green-700 font-semibold bg-green-100 px-3 py-2 rounded border-2 border-green-200">
                                  Ready from Stock
                                </span>
                                <span className="text-green-600 text-lg">✅</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <input
                                  type="date"
                                  className={`input-field text-sm w-full sm:w-auto ${
                                    !hasDate ? 'border-orange-300' : ''
                                  }`}
                                  value={itemDeliveryDates[item.id] || ''}
                                  min={orderDate}
                                  onChange={(e) => setItemDeliveryDates({ 
                                    ...itemDeliveryDates, 
                                    [item.id]: e.target.value 
                                  })}
                                />
                                {hasDate && (
                                  <span className="text-green-600 text-lg">✅</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Apply to All Button - only for non-ready items */}
                  {items.filter(i => !i.ready_item).length > 1 && 
                   Object.entries(itemDeliveryDates).some(([id, date]) => {
                     const item = items.find(i => i.id === id);
                     return date && item && !item.ready_item;
                   }) && (
                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          // Find the first set date from non-ready items
                          const firstDate = Object.entries(itemDeliveryDates).find(([id, date]) => {
                            const item = items.find(i => i.id === id);
                            return date && item && !item.ready_item;
                          })?.[1];
                          
                          if (firstDate) {
                            const newDates = { ...itemDeliveryDates };
                            items.forEach(item => {
                              if (!item.ready_item) {
                                newDates[item.id] = firstDate;
                              }
                            });
                            setItemDeliveryDates(newDates);
                          }
                        }}
                        className="w-full py-2 px-4 bg-amber-100 border-2 border-amber-300 text-amber-700 rounded-lg text-sm font-semibold hover:bg-amber-200 transition-colors"
                      >
                        Apply First Date to All Non-Ready Items
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECTION 4: PAYMENT */}
        <div ref={paymentRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('payment')}
          >
            <h2 className="text-base font-bold text-gray-800">Payment & Advance</h2>
            {expandedSections.payment ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.payment && (
            <div className="p-4 space-y-3">
              {/* Summary Info */}
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-xs text-gray-600">Subtotal: <span className="font-bold text-gray-800">₹{(subtotal || 0).toFixed(2)}</span></p>
                <p className="text-xs text-gray-600">Advance Paid: <span className="font-bold text-green-700">₹{(advancePaid || 0).toFixed(2)}</span></p>
                {metalAdvance > 0 && (
                  <p className="text-xs text-gray-600">Metal Credit (equiv): <span className="font-bold text-blue-700">₹{(metalAdvance || 0).toFixed(2)}</span></p>
                )}
                {metalAdvance > 0 && (
                  <p className="text-xs text-gray-600">Total Advance: <span className="font-bold text-green-800">₹{(totalAdvance || 0).toFixed(2)}</span></p>
                )}
                <p className="text-xs text-gray-600">Balance: <span className="font-bold text-orange-700">₹{((subtotal || 0) - (advancePaid || 0)).toFixed(2)}</span></p>
                {metalCreditBalance > 0 && (
                  <p className="text-xs text-gray-600">Metal Credit: <span className="font-bold text-blue-700">{(metalCreditBalance || 0).toFixed(2)}g</span></p>
                )}
                {subtotal > 0 && totalAdvance > 0 && (
                  <div className="mt-2 pt-2 border-t border-amber-200">
                    <p className="text-xs font-semibold">
                      {totalAdvance >= subtotal * 0.8 ? (
                        <span className="text-green-700">✓ Advance met: {(advancePercent || 0).toFixed(0)}% {metalAdvance > 0 && '(incl. metal credit)'}</span>
                      ) : (
                        <span className="text-orange-700">⚠ Advance: {(advancePercent || 0).toFixed(0)}% {metalAdvance > 0 && '(incl. metal credit)'} - Need {(80 - (advancePercent || 0)).toFixed(0)}% more</span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Payment Summary */}
              <PaymentSummary
                payments={payments}
                onEdit={handlePaymentEdit}
                onDelete={handlePaymentDelete}
                onWhatsApp={() => {}}
                customerPhone={customers.find(c => c.id === selectedCustomer)?.phone}
              />

              {/* Add Payment Button */}
              <button
                type="button"
                onClick={() => setShowPaymentTypeSelector(true)}
                className="btn-primary w-full text-sm py-3 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Payment/Deposit
              </button>

              {/* Warning if underpaid */}
              {totalAdvance < subtotal * 0.8 && totalAdvance > 0 && (
                <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-2">
                  <p className="text-xs text-orange-700 font-semibold">⚠️ Underpaid Order</p>
                  <p className="text-xs text-orange-600">Need ₹{((subtotal || 0) * 0.8 - (totalAdvance || 0)).toFixed(2)} more (cash or metal credit)</p>
                </div>
              )}

              {/* Payment Reminder Card */}
              <PaymentReminderCard
                isExpanded={showPaymentReminder || (ratePolicy === 'lock-today' && totalAdvance < subtotal * 0.8)}
                onToggle={() => setShowPaymentReminder(!showPaymentReminder)}
                advancePaid={totalAdvance}
                requiredAdvance={subtotal * 0.8}
                selectedCustomer={selectedCustomer}
                customers={customers}
                orderDate={orderDate}
                onReminderChange={setPaymentReminderData}
                initialData={paymentReminderData}
              />

              {/* Signatures Section - Inline */}
              <div className="bg-white rounded-xl shadow-md p-6 mt-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">✍️ Signatures</h3>
                
                <div className="space-y-4">
                  {/* Management Signature */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Management Signature
                    </label>
                    <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                      {managementSignature ? (
                        <div className="relative p-4">
                          <img src={managementSignature} alt="Management Signature" className="w-full h-32 object-contain border border-gray-200 rounded" />
                          <div className="flex gap-2 mt-3">
                            <button
                              type="button"
                              onClick={() => setShowManagementSignatureModal(true)}
                              className="flex-1 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 font-semibold"
                            >
                              Edit Signature
                            </button>
                            <button
                              type="button"
                              onClick={() => setManagementSignature(null)}
                              className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 font-semibold"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowManagementSignatureModal(true)}
                          className="w-full p-8 flex flex-col items-center justify-center gap-3 hover:bg-amber-50 transition-colors"
                        >
                          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                            <span className="text-3xl">✍️</span>
                          </div>
                          <span className="text-amber-600 font-semibold">Tap to Add Management Signature</span>
                          <span className="text-xs text-gray-500">Opens fullscreen signing pad</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Customer Signature */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Customer Signature
                    </label>
                    <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                      {customerSignature ? (
                        <div className="relative p-4">
                          <img src={customerSignature} alt="Customer Signature" className="w-full h-32 object-contain border border-gray-200 rounded" />
                          <div className="flex gap-2 mt-3">
                            <button
                              type="button"
                              onClick={() => setShowCustomerSignatureModal(true)}
                              className="flex-1 px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 font-semibold"
                            >
                              Edit Signature
                            </button>
                            <button
                              type="button"
                              onClick={() => setCustomerSignature(null)}
                              className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 font-semibold"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowCustomerSignatureModal(true)}
                          className="w-full p-8 flex flex-col items-center justify-center gap-3 hover:bg-blue-50 transition-colors"
                        >
                          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-3xl">✍️</span>
                          </div>
                          <span className="text-blue-600 font-semibold">Tap to Add Customer Signature</span>
                          <span className="text-xs text-gray-500">Opens fullscreen signing pad</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Bottom Summary Bar with Single Review Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-200 shadow-lg p-3 z-30">
        <div className="flex items-center justify-between">
          {/* Summary Info */}
          <div className="flex gap-3 sm:gap-4 text-xs">
            <div>
              <p className="text-gray-500">Subtotal</p>
              <p className="font-bold">₹{(subtotal || 0).toFixed(0)}</p>
            </div>
            <div>
              <p className="text-gray-500">Advance</p>
              <p className="font-bold text-green-600">₹{(advancePaid || 0).toFixed(0)}</p>
            </div>
            <div>
              <p className="text-gray-500">Balance</p>
              <p className="font-bold text-amber-600">₹{(balance || 0).toFixed(0)}</p>
            </div>
            {metalAdvance > 0 && (
              <div>
                <p className="text-gray-500">Metal</p>
                <p className="font-bold text-blue-600">{(metalCreditBalance || 0).toFixed(1)}g</p>
              </div>
            )}
          </div>

          {/* Single Action Button */}
          <button
            type="button"
            onClick={() => setShowReviewPage(true)}
            disabled={!selectedCustomer || !orderDate}
            className="px-6 py-2.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg text-sm font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-xl transition-all"
          >
            Review & Finalize
          </button>
        </div>
      </div>

      {/* Modals */}
      {showCustomerModal && (
        <CustomerSelectionModal
          onClose={() => setShowCustomerModal(false)}
          onCustomerSelected={(customer) => {
            // If this is a newly added customer, add it to the list
            if (!customers.find(c => c.id === customer.id)) {
              setCustomers([...customers, customer]);
            }
            setSelectedCustomer(customer.id);
            setCustomerSearch(customer.name);
            setShowCustomerModal(false);
          }}
          customers={customers}
          selectedCustomerId={selectedCustomer}
        />
      )}

      {showEditCustomerModal && editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
          }}
          onCustomerUpdated={(updatedCustomer) => {
            // Update the customer in the list
            setCustomers(customers.map(c => 
              c.id === updatedCustomer.id ? updatedCustomer : c
            ));
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
          }}
        />
      )}

      {editingImage && (
        <ImageEditorModal
          imageData={editingImage.imageData}
          onClose={() => setEditingImage(null)}
          onSave={(editedImage) => {
            if (editingImage.photoIndex !== undefined) {
              handleImageEdited(editingImage.itemId, editingImage.photoIndex, editedImage);
            } else {
              handleImageSaved(editingImage.itemId, editedImage);
            }
            setEditingImage(null);
          }}
        />
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10 shadow-lg"
              aria-label="Close preview"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Modal Pickers */}
      <QuantityPickerModal
        isOpen={activeModal === 'quantity'}
        onClose={() => {
          setActiveModal(null);
          setModalItemId(null);
        }}
        onSelect={(value) => {
          if (modalItemId) {
            updateItem(modalItemId, 'quantity', value);
          }
        }}
        currentValue={modalItemId ? items.find(i => i.id === modalItemId)?.quantity : '1'}
      />

      <PurityPickerModal
        isOpen={activeModal === 'purity'}
        onClose={() => {
          setActiveModal(null);
          setModalItemId(null);
        }}
        onSelect={(value) => {
          if (modalItemId) {
            updateItem(modalItemId, 'purity', value);
          }
        }}
        currentValue={modalItemId ? items.find(i => i.id === modalItemId)?.purity : '22K'}
        purities={purityOptions}
      />

      <FinishColorPickerModal
        isOpen={activeModal === 'finish'}
        onClose={() => {
          setActiveModal(null);
          setModalItemId(null);
        }}
        onSelect={(value) => {
          if (modalItemId) {
            updateItem(modalItemId, 'finish_color', value);
          }
        }}
        currentValue={modalItemId ? items.find(i => i.id === modalItemId)?.finish_color : 'Yellow Gold'}
        colors={finishColors}
      />

      <VendorPickerModal
        isOpen={activeModal === 'vendor'}
        onClose={() => {
          setActiveModal(null);
          setModalItemId(null);
        }}
        onSelect={(value) => {
          if (modalItemId) {
            updateItem(modalItemId, 'vendor_short_code', value);
          }
        }}
        currentValue={modalItemId ? items.find(i => i.id === modalItemId)?.vendor_short_code : ''}
        vendors={vendors}
      />

      {/* Payment Modals */}
      <PaymentTypeSelector
        isOpen={showPaymentTypeSelector}
        onClose={() => setShowPaymentTypeSelector(false)}
        onSelectType={(type) => setActivePaymentModal(type)}
      />

      <CashPaymentModal
        isOpen={activePaymentModal === 'cash'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <CardPaymentModal
        isOpen={activePaymentModal === 'card'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <UPIPaymentModal
        isOpen={activePaymentModal === 'upi'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <BankTransferModal
        isOpen={activePaymentModal === 'bank'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <OldGoldModal
        isOpen={activePaymentModal === 'old-gold'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? {
          ...payments[editingPaymentIndex],
          items: payments[editingPaymentIndex]?.items || [] // Ensure items array exists
        } : null}
      />

      <MetalCreditModal
        isOpen={activePaymentModal === 'metal'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
        base24kRate={base24kRate}
        gstInputStyle={gstInputStyle}
      />
      
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={cancelDelete}>
          <div 
            className="bg-white rounded-2xl w-full max-w-sm mx-4 p-6 space-y-4" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Delete Payment?</h3>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this payment entry? This action cannot be undone.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={cancelDelete}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Approval Modal for Back-dating */}
      {/* Item Type Modal Picker */}
      {showItemTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b-2 border-amber-200 flex justify-between items-center bg-gradient-to-r from-amber-100 to-orange-100">
              <h3 className="text-lg font-bold text-gray-800">Select Item Type</h3>
              <button 
                type="button"
                onClick={() => {
                  setShowItemTypeModal(false);
                  setItemTypeSearch('');
                }}
                className="p-1 hover:bg-amber-200 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <input
                type="text"
                className="input-field w-full"
                placeholder="Search item types..."
                value={itemTypeSearch}
                onChange={(e) => setItemTypeSearch(e.target.value)}
              />
            </div>

            {/* Item Types Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {itemTypes
                  .filter(type => type.toLowerCase().includes(itemTypeSearch.toLowerCase()))
                  .map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        if (itemTypeModalTargetId) {
                          updateItem(itemTypeModalTargetId, 'item_type', type);
                        }
                        setShowItemTypeModal(false);
                        setItemTypeSearch('');
                        setItemTypeModalTargetId(null);
                      }}
                      className="p-4 border-2 border-amber-200 rounded-lg hover:bg-amber-50 transition-all hover:border-amber-400 hover:shadow-md"
                    >
                      <div className="text-sm font-semibold text-gray-800">{type}</div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review & Finalize Page */}
      {showReviewPage && (
        <ReviewPageErrorBoundary onClose={() => setShowReviewPage(false)}>
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-amber-100 to-orange-100 border-b-2 border-amber-200 p-4 shadow-md">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Review Order</h2>
              <button 
                type="button"
                onClick={() => setShowReviewPage(false)}
                className="p-2 hover:bg-amber-200 rounded-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4 max-w-3xl mx-auto pb-32">
            {/* Order Header Info */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Order Information</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-600 text-xs">Customer</p>
                  <p className="font-semibold">{customers.find(c => c.id === selectedCustomer)?.name || 'Not selected'}</p>
                  {(customers.find(c => c.id === selectedCustomer)?.phone || customers.find(c => c.id === selectedCustomer)?.mobile) && (
                    <p className="text-xs text-gray-600">
                      {customers.find(c => c.id === selectedCustomer)?.phone || customers.find(c => c.id === selectedCustomer)?.mobile}
                    </p>
                  )}
                  {customers.find(c => c.id === selectedCustomer)?.alt_phone && (
                    <p className="text-xs text-gray-600">Alt: {customers.find(c => c.id === selectedCustomer)?.alt_phone}</p>
                  )}
                  {customers.find(c => c.id === selectedCustomer)?.email && (
                    <p className="text-xs text-gray-600 break-all">✉️ {customers.find(c => c.id === selectedCustomer)?.email}</p>
                  )}
                  {customers.find(c => c.id === selectedCustomer)?.address && (
                    <p className="text-xs text-gray-600 break-words">📍 {customers.find(c => c.id === selectedCustomer)?.address}</p>
                  )}
                </div>
                <div>
                  <p className="text-gray-600 text-xs">Order Date</p>
                  <p className="font-semibold">
                    {new Date(orderDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {orderDate < new Date().toISOString().split('T')[0] && (
                      <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded">Back-dated</span>
                    )}
                  </p>
                </div>
              </div>
              <button 
                type="button"
                onClick={() => {
                  setShowReviewPage(false);
                  scrollToSection(itemsRef, 'items');
                }}
                className="mt-3 text-xs text-amber-600 font-semibold hover:text-amber-700"
              >
                ✏️ Edit
              </button>
            </div>

            {/* Items */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800">Items ({items.length})</h3>
                <button 
                  type="button"
                  onClick={() => {
                    setShowReviewPage(false);
                    scrollToSection(itemsRef, 'items');
                  }}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                >
                  ✏️ Edit
                </button>
              </div>
              <div className="space-y-3">
                {items.map((item, idx) => {
                  const itemRate = rateMode === 'single' 
                    ? (parseFloat(purityRates[item.purity]) || parseFloat(base24kRate) || 0)
                    : parseFloat(itemRates[item.id] || 0);
                  
                  // Display rate is always the raw input rate
                  const displayRate = parseFloat(itemRate) || 0;
                  
                  // Calculation rate includes GST if excluding
                  let calcRate = itemRate;
                  if (gstInputStyle === 'EXCL' && itemRate > 0) {
                    calcRate = itemRate * 1.03;
                  }
                  
                  const weight = item.weight_type === 'Fixed' 
                    ? parseFloat(item.weight_fixed) 
                    : (parseFloat(item.weight_min) + parseFloat(item.weight_max)) / 2;
                  const quantity = parseFloat(item.quantity || 1);
                  const making = parseFloat(item.making_value || 0);
                  
                  // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
                  let itemSubtotal;
                  if (item.making_type === 'WASTAGE') {
                    let rate24k = parseFloat(base24kRate) || 0;
                    if (gstInputStyle === 'EXCL' && rate24k > 0) {
                      rate24k = rate24k * 1.03;
                    }
                    itemSubtotal = weight * rate24k * (making / 100);
                  } else {
                    itemSubtotal = weight * calcRate;
                    if (item.making_type === 'PERCENT') {
                      itemSubtotal += itemSubtotal * (making / 100);
                    } else if (item.making_type === 'PER_GRAM') {
                      itemSubtotal += weight * making;
                    } else if (item.making_type === 'FLAT') {
                      itemSubtotal += making;
                    }
                  }
                  
                  return (
                    <div key={item.id} className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 text-xs">
                      {/* Header with Item Number and Ready Badge */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="bg-amber-600 text-white px-2 py-0.5 rounded-full font-bold">#{idx + 1}</span>
                          <span className="font-semibold text-sm">{item.item_type || 'Select Item Type'}</span>
                        </div>
                        {item.ready_item && (
                          <span className="bg-green-500 text-white px-2 py-0.5 rounded font-semibold">READY</span>
                        )}
                      </div>

                      {/* Item Details Grid */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div><span className="text-gray-600">Weight:</span> <span className="font-medium">{item.weight_type === 'Fixed' ? `${item.weight_fixed}g` : `${item.weight_min}-${item.weight_max}g`}</span></div>
                        <div><span className="text-gray-600">Purity:</span> <span className="font-medium">{item.purity}</span></div>
                        <div><span className="text-gray-600">Quantity:</span> <span className="font-medium">{quantity} pc</span></div>
                        <div><span className="text-gray-600">Making:</span> <span className="font-medium">{item.making_type === 'PERCENT' ? `${making}%` : item.making_type === 'PER_GRAM' ? `₹${making}/g` : item.making_type === 'WASTAGE' ? `${making} %Wst` : `₹${making}`}</span></div>
                        
                        {/* Size */}
                        {item.size && !item.ready_item && (
                          <div><span className="text-gray-600">Size:</span> <span className="font-medium">{item.size}</span></div>
                        )}
                        
                        {/* Finish Color */}
                        {item.finish_color && !item.ready_item && (
                          <div><span className="text-gray-600">Finish:</span> <span className="font-medium">{item.finish_color}</span></div>
                        )}
                        
                        {/* Vendor */}
                        {item.vendor_short_code && (
                          <div className="col-span-2"><span className="text-gray-600">Vendor:</span> <span className="font-medium">{item.vendor_short_code}</span></div>
                        )}
                        
                        {/* Tag Number for Ready Items */}
                        {item.ready_item && item.tag_number && (
                          <div className="col-span-2"><span className="text-gray-600">Tag #:</span> <span className="font-medium">{item.tag_number}</span></div>
                        )}
                        
                        {/* Public Remarks - inline with other details */}
                        {item.public_remark && (
                          <div className="col-span-2"><span className="text-gray-600">Public Remarks:</span> <span className="font-medium">{item.public_remark}</span></div>
                        )}
                        
                        {/* Rate with GST info */}
                        {ratePolicy === 'rate-at-delivery' ? (
                          <div className="col-span-2">
                            <span className="text-gray-600">Rate:</span>
                            <span className="font-semibold text-amber-700 ml-1">Rate at Delivery</span>
                          </div>
                        ) : (
                          <div className="col-span-2">
                            <span className="text-gray-600">Rate:</span> 
                            <span className="font-semibold text-green-700"> ₹{displayRate.toFixed(2)}/g</span>
                            <span className="text-gray-500 ml-1">
                              ({gstInputStyle === 'INCL' ? 'incl GST' : 'ex GST'})
                            </span>
                          </div>
                        )}
                        
                        {/* Delivery Date */}
                        {!item.ready_item && (
                          <div className="col-span-2">
                            <span className="text-gray-600">Delivery:</span> 
                            <span className="font-medium">
                              {deliveryMode === 'single' 
                                ? (singleDeliveryDate ? new Date(singleDeliveryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set')
                                : (itemDeliveryDates[item.id] ? new Date(itemDeliveryDates[item.id]).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set')
                              }
                            </span>
                          </div>
                        )}
                        {item.ready_item && (
                          <div className="col-span-2">
                            <span className="text-gray-600">Delivery:</span> 
                            <span className="font-medium text-green-700">Ready from Stock</span>
                          </div>
                        )}
                      </div>

                      {/* Photos */}
                      {item.photos && item.photos.length > 0 && (
                        <div className="border-t border-amber-300 pt-2 mt-2">
                          <p className="text-gray-600 mb-1 font-medium">Photos ({item.photos.length}):</p>
                          <div className="flex gap-2 flex-wrap">
                            {item.photos.map((img, imgIdx) => (
                              <img
                                key={imgIdx}
                                src={img}
                                alt={`Item ${idx + 1} - Photo ${imgIdx + 1}`}
                                className="w-16 h-16 object-cover rounded border-2 border-amber-300 cursor-pointer hover:border-amber-500 transition-all hover:scale-105"
                                onClick={() => setPreviewImage(img)}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Internal Remarks */}
                      {item.internal_remark && (
                        <div className="border-t border-amber-300 pt-2 mt-2">
                          <p className="text-gray-600 font-medium">Internal Remarks:</p>
                          <p className="font-medium text-red-700 mt-1">{item.internal_remark}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rates & Policy */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800">Rates & Policy</h3>
                <button 
                  type="button"
                  onClick={() => {
                    setShowReviewPage(false);
                    scrollToSection(ratesRef, 'rates');
                  }}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                >
                  ✏️ Edit
                </button>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base 24K Rate:</span>
                  <span className="font-semibold">₹{base24kRate || 0}/g {gstInputStyle === 'EXCL' ? '(ex GST)' : '(incl GST)'}</span>
                </div>
                
                {/* Show all purity rates if available */}
                {Object.keys(purityRates).length > 0 && (
                  <div className="bg-amber-50 rounded p-2 mt-2">
                    <p className="font-semibold text-gray-700 mb-1">Purity Rates:</p>
                    {Object.entries(purityRates).map(([purity, rate]) => (
                      <div key={purity} className="flex justify-between text-xs">
                        <span className="text-gray-600">{purity}:</span>
                        <span className="font-medium">₹{parseFloat(rate).toFixed(2)}/g</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Rate Policy:</span>
                  <span className="font-semibold capitalize">{(ratePolicy || '').replace('-', ' ')}</span>
                </div>
                {ratePolicy === 'lock-today' && lockDate && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                    🔒 Locked — 24K = ₹{base24kRate}/g {gstInputStyle === 'INCL' && '(incl 3% GST)'} on {new Date(lockDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>

            {/* Payments */}
            <div className="premium-card border-2 border-amber-200 p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-gray-800">Payments & Deposits ({payments.length})</h3>
                <button 
                  type="button"
                  onClick={() => {
                    setShowReviewPage(false);
                    scrollToSection(paymentRef, 'payment');
                  }}
                  className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                >
                  ✏️ Edit
                </button>
              </div>
              {payments.length > 0 ? (
                <div className="space-y-3">
                  {payments.map((payment, idx) => {
                    const isRefund = payment.transaction_type === 'REFUND';
                    return (
                    <div key={idx} className={`rounded p-3 text-xs border-2 ${isRefund ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className={`font-semibold text-sm ${isRefund ? 'text-red-700' : 'text-gray-800'}`}>
                            {isRefund ? '↩️ REFUND - ' : ''}{payment.mode}
                          </span>
                          {payment.date && <span className="text-gray-600 ml-2">• {new Date(payment.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                        </div>
                        <div className={`font-bold ${isRefund ? 'text-red-700' : 'text-amber-700'}`}>
                          {payment.amount && `${isRefund ? '-' : ''}₹${parseFloat(payment.amount).toLocaleString('en-IN')}`}
                          {payment.fine_wt && ` ${parseFloat(payment.fine_wt).toFixed(2)}g`}
                        </div>
                      </div>
                      
                      {/* Payment Details */}
                      {payment.note && (
                        <div className="text-gray-600 mb-1">
                          <span className="font-medium">Note:</span> {payment.note}
                        </div>
                      )}
                      
                      {payment.reference && (
                        <div className="text-gray-600 mb-1">
                          <span className="font-medium">Reference:</span> {payment.reference}
                        </div>
                      )}
                      
                      {/* Card Payment Details */}
                      {payment.mode === 'Card' && (
                        <div className="text-gray-600">
                          {payment.card_last4 && <div>Card: ...{payment.card_last4}</div>}
                          {payment.pos_ref && <div>POS Ref: {payment.pos_ref}</div>}
                        </div>
                      )}
                      
                      {/* UPI Details */}
                      {payment.mode === 'UPI' && (
                        <div className="text-gray-600">
                          {payment.upi_ref && <div>UPI Ref: {payment.upi_ref}</div>}
                          {payment.upi_handle && <div>Handle: {payment.upi_handle}</div>}
                        </div>
                      )}
                      
                      {/* Bank Transfer Details */}
                      {(payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS' || payment.mode === 'Cheque') && (
                        <div className="text-gray-600">
                          {payment.utr_ref && <div>UTR/Ref: {payment.utr_ref}</div>}
                          {payment.bank_name && <div>Bank: {payment.bank_name}</div>}
                          {payment.payer_acct_last4 && <div>A/c: ...{payment.payer_acct_last4}</div>}
                          {payment.cheque_no && <div>Cheque #: {payment.cheque_no}</div>}
                          {payment.cheque_status && <div>Status: {payment.cheque_status}</div>}
                        </div>
                      )}
                      
                      {/* Old Gold Breakdown */}
                      {payment.mode === 'Old Gold' && payment.items && payment.items.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-amber-300">
                          <p className="font-semibold text-gray-700 mb-2">Old Gold Items:</p>
                          {payment.items.map((item, itemIdx) => (
                            <div key={itemIdx} className="bg-white rounded p-2 mb-1 text-xs">
                              <div className="font-semibold">{item.item_name || `Item ${itemIdx + 1}`}</div>
                              <div className="grid grid-cols-2 gap-1 mt-1">
                                <div>Gross: {parseFloat(item.gross_wt || 0).toFixed(3)}g</div>
                                <div>Less: {parseFloat(item.less_wt || 0).toFixed(3)}g</div>
                                <div>Net: {parseFloat(item.net_wt || 0).toFixed(3)}g</div>
                                <div>Purity: {parseFloat(item.purity || 0).toFixed(1)}%</div>
                                <div>Fine: {parseFloat(item.fine_wt || 0).toFixed(3)}g</div>
                                <div>@ ₹{parseFloat(item.purchase_rate || 0).toFixed(0)}/g</div>
                              </div>
                              <div className="mt-1 font-semibold text-amber-700">
                                Total: ₹{parseFloat(item.line_total || 0).toFixed(2)}
                              </div>
                            </div>
                          ))}
                          <div className="mt-2 pt-2 border-t border-amber-300 font-bold">
                            Total Fine: {payment.fine_grams ? parseFloat(payment.fine_grams).toFixed(3) : '0.000'}g • Total: ₹{payment.amount ? parseFloat(payment.amount).toLocaleString('en-IN') : 0}
                          </div>
                        </div>
                      )}
                      
                      {/* Fine Metal Breakdown */}
                      {payment.mode === 'Metal Credit' && payment.deposits && payment.deposits.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-amber-300">
                          <p className="font-semibold text-gray-700 mb-2">Metal Deposits:</p>
                          {payment.deposits.map((deposit, depIdx) => (
                            <div key={depIdx} className="bg-white rounded p-2 mb-1 text-xs">
                              <div className="font-semibold">
                                {deposit.type === 'cadbury' ? 'Cadbury' : 
                                 deposit.type === 'rava' ? 'Rava' :
                                 deposit.type === 'old-gold' ? 'Old Gold (Jewellery)' :
                                 deposit.type === 'akj-new' ? 'AKJ New' :
                                 deposit.type_name_other || deposit.type}
                                {deposit.item_name && ` - ${deposit.item_name}`}
                              </div>
                              <div className="grid grid-cols-2 gap-1 mt-1">
                                {deposit.gross_wt && <div>Gross: {parseFloat(deposit.gross_wt).toFixed(3)}g</div>}
                                {deposit.less_wt && <div>Less: {parseFloat(deposit.less_wt).toFixed(3)}g</div>}
                                <div>Net: {parseFloat(deposit.net_wt || 0).toFixed(3)}g</div>
                                <div>Purity: {parseFloat(deposit.purity || 0).toFixed(1)}%</div>
                              </div>
                              <div className="mt-1 font-semibold text-blue-700">
                                Fine: {parseFloat(deposit.fine_wt || 0).toFixed(3)}g
                              </div>
                              {deposit.reference && (
                                <div className="text-gray-600 mt-1">Ref: {deposit.reference}</div>
                              )}
                              {deposit.photos && deposit.photos.length > 0 && (
                                <div className="mt-2 border-t border-gray-200 pt-2">
                                  <p className="text-gray-600 mb-1">Photos ({deposit.photos.length}):</p>
                                  <div className="flex gap-1 flex-wrap">
                                    {deposit.photos.map((photo, photoIdx) => {
                                      // Handle both old format (string) and new format (object with original/edited)
                                      const displayImage = typeof photo === 'string' ? photo : photo.edited;
                                      return (
                                        <img
                                          key={photoIdx}
                                          src={displayImage}
                                          alt={`Photo ${photoIdx + 1}`}
                                          className="w-12 h-12 object-cover rounded border border-amber-300 cursor-pointer hover:border-amber-500"
                                          onClick={() => setPreviewImage(displayImage)}
                                          title={typeof photo === 'object' ? 'Edited image (original preserved for audit)' : ''}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                          <div className="mt-2 pt-2 border-t border-amber-300 font-bold">
                            Total Fine: {payment.fine_wt ? parseFloat(payment.fine_wt).toFixed(3) : '0.000'}g
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No payments added</p>
              )}

              {/* Payment Reminder Display */}
              {paymentReminderData && paymentReminderData.enabled && (
                <div className="mt-3 bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-blue-800">💰 Payment Reminder Set</span>
                    <button 
                      type="button"
                      onClick={() => {
                        setShowReviewPage(false);
                        setShowPaymentReminder(true);
                        scrollToSection(paymentRef, 'payment');
                      }}
                      className="text-xs text-blue-600 font-semibold hover:text-blue-700"
                    >
                      ✏️ Edit
                    </button>
                  </div>
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Due:</span>
                      <span className="font-semibold">{new Date(paymentReminderData.due_at).toLocaleString('en-IN', { 
                        day: 'numeric', 
                        month: 'short', 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: true 
                      })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-bold text-blue-700">₹{parseFloat(paymentReminderData.amount_due).toLocaleString('en-IN')}</span>
                    </div>
                    {paymentReminderData.channels && paymentReminderData.channels.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Via:</span>
                        <span className="font-medium">{paymentReminderData.channels.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Signatures Display */}
              <div className="mt-3 bg-purple-50 border-2 border-purple-200 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-purple-800 mb-2">✍️ Signatures</h4>
                
                {managementSignature || customerSignature ? (
                  <div className="grid grid-cols-2 gap-2">
                    {managementSignature && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Management</p>
                        <div className="bg-white rounded border border-purple-300 p-2">
                          <img src={managementSignature} alt="Management" className="w-full h-16 object-contain" />
                          {managementSignatureName && (
                            <p className="text-xs text-center text-gray-700 mt-1 font-semibold">{managementSignatureName}</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {customerSignature && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Customer</p>
                        <div className="bg-white rounded border border-purple-300 p-2">
                          <img src={customerSignature} alt="Customer" className="w-full h-16 object-contain" />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No signatures added</p>
                )}
                
                <button 
                  type="button"
                  onClick={() => setShowReviewPage(false)}
                  className="text-xs text-purple-600 font-semibold hover:text-purple-700 mt-2"
                >
                  {managementSignature || customerSignature ? 'Edit Signatures' : 'Add Signatures'}
                </button>
              </div>
            </div>

            {/* Totals */}
            <div className="premium-card border-2 border-green-200 bg-green-50 p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Subtotal:</span>
                  <span className="font-bold">₹{(subtotal || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Advance Paid:</span>
                  <span className="font-bold text-green-700">₹{(advancePaid || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                {metalAdvance > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Metal Credit:</span>
                      <span className="font-bold text-blue-700">{(metalCreditBalance || 0).toFixed(2)}g (≈₹{(metalAdvance || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})})</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Total Advance:</span>
                      <span className="font-bold text-green-800">₹{(totalAdvance || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between border-t-2 border-green-300 pt-2">
                  <span className="text-gray-800 font-semibold">Balance Due:</span>
                  <span className="font-bold text-amber-700 text-lg">₹{(balance || 0).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                {subtotal > 0 && totalAdvance > 0 && (
                  <div className="bg-white rounded p-2 text-xs">
                    {totalAdvance >= subtotal * 0.8 ? (
                      <span className="text-green-700 font-semibold">✓ Advance requirement met: {(advancePercent || 0).toFixed(0)}%</span>
                    ) : (
                      <span className="text-orange-700 font-semibold">⚠ Advance: {(advancePercent || 0).toFixed(0)}% - Need {(80 - (advancePercent || 0)).toFixed(0)}% more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sticky Footer with Finalize Button */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-200 shadow-lg p-4 z-30">
            <div className="max-w-3xl mx-auto flex gap-3">
              <button
                type="button"
                onClick={() => setShowReviewPage(false)}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300"
              >
                Back to Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowReviewPage(false);
                  handleFinalizeOrder();
                }}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating Order...
                  </>
                ) : (
                  'Finalize Order'
                )}
              </button>
            </div>
          </div>
        </div>
        </ReviewPageErrorBoundary>
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          onClose={() => setShowSignatureModal(false)}
          onConfirm={handleSignatureConfirm}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && createdOrderData && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Order Created Successfully!</h2>
            </div>
            
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Order ID</p>
              <p className="text-xl font-bold text-amber-700 mb-3">{createdOrderData.order_id}</p>
              
              <p className="text-sm text-gray-600 mb-1">Customer</p>
              <p className="text-lg font-semibold text-gray-800">{createdOrderData.customer_name}</p>
            </div>

            <button
              type="button"
              onClick={handleSuccessClose}
              className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-bold shadow-lg hover:shadow-xl transition-all"
            >
              Create New Order
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Order Created Successfully!</h2>
              <p className="text-gray-600">Order ID: <span className="font-semibold">{createdOrderId}</span></p>
              <p className="text-sm text-gray-500 mt-2">What would you like to do next?</p>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/submissions/my-all');
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Go to My Submissions
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/');
                }}
                className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit confirmation modal removed with useBlocker */}

      {/* Signature Modals */}
      <SignatureModal
        isOpen={showManagementSignatureModal}
        onClose={() => setShowManagementSignatureModal(false)}
        onSave={(signatureData) => {
          setManagementSignature(signatureData);
          setShowManagementSignatureModal(false);
          toast.success('Management signature saved!');
        }}
        title="Management Signature"
        existingSignature={managementSignature}
      />

      <SignatureModal
        isOpen={showCustomerSignatureModal}
        onClose={() => setShowCustomerSignatureModal(false)}
        onSave={(signatureData) => {
          setCustomerSignature(signatureData);
          setShowCustomerSignatureModal(false);
          toast.success('Customer signature saved!');
        }}
        title="Customer Signature"
        existingSignature={customerSignature}
      />
    </div>
  );
};

export default CreateOrderStepper;
